import moment from 'moment';
import { flatten, groupBy } from 'lodash';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { saveAs } from 'file-saver';
import exportExcel from '@ubeya/shared/utils/exportExcel';
import exportTxt from '@ubeya/shared/utils/exportTxt';
import smartExportExcel from '@ubeya/shared/utils/smartExportExcel';
import { generateRange, getDurationFromMilliseconds, getMillisecondsFromDecimal } from '@ubeya/shared/utils/time';
import {
  API_DATE_FORMAT,
  API_DATE_TIME_FORMAT,
  PAYROLL_FIELD_TIP_BONUS_ID,
  PAYROLL_FIELD_TIP_ID,
  PAYROLL_FIELD_TIP_ID_7036,
  PAYROLL_FIELD_TRAVEL_ID,
  PAYROLL_FIELD_TRAVEL_PARKING_ID,
  PTO_TYPES,
  TIMESHEET_GROUP_BY_OPTIONS_ENUM
} from '@ubeya/shared/constants';
import { SORT_BY_DATE, SORT_BY_NAME, SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import {
  CHEFS_GARNED_CATERING_AND_EVENTS_ACCOUNT_ID,
  DYNAMO_ACCOUNT_ID,
  ETZ_HASADE_ACCOUNT_ID,
  FRIENDS_ACCOUNT_ID,
  KAPAIM_ACCOUNT_ID,
  LABOR_DESK_ACCOUNT_ID,
  OBE_ACCOUNT_ID,
  PRICELESS_ONE_ACCOUNT_ID,
  SURREY_COUNTY_CRICKET_CLUB,
  YORDEI_HASIRA_ACCOUNT_ID
} from '@ubeya/shared/utils/accounts';
import { mappedArray } from '@ubeya/shared/utils/array';
import { generateShiftName } from '@ubeya/shared/utils/scheduling';
import { getTimesheetFieldsForExport } from '@ubeya/shared/utils/timesheetFields';
import {
  LEAVE_TYPE_NAME_TO_SUMMARIZED_COLUMN,
  PAYROLL_CUSTOM_FIELD_TYPE,
  SUMMARIZED_SICK_HOURS_COLUMN,
  SUMMARIZED_VACATION_HOURS_COLUMN
} from 'shared/constants/payroll';
import groupByDataTimesheet, {
  filterData,
  formatProjectDataSlots,
  getEmployeesMap,
  MAP_GROUP,
  totalTime
} from './timesheet';
import { columnHeaderKey, conditionalColumns } from './csvColumns';
import {
  buildFriendsShiklulitDataRows,
  buildFriendsShiklulitRowContextsForEmployee,
  MEGA_PAY_CODES,
  MICHPAL,
  ON_PAY_PAYROLL_CODES,
  SHIKLULIT,
  TEMP_STAFF_PAYROLL_REPORT_CODES
} from './payrollCodes/codes';
import { clientInvoicesExcel, exportExcelOB } from './payroll-export-generator';
import {
  HARD_CODED_ISRAEL_VAT,
  HARD_CODED_ISRAEL_VAT_PRECENTAGE,
  HARD_CODED_LABOR_DESK_VAT,
  HARD_CODED_LABOR_DESK_VAT_PRECENTAGE
} from 'payroll/constants';
import { charPad } from 'payroll/utils';

const addedColumnsPerAccount = ({ accountId, row, employee }) => {
  switch (accountId) {
    case DYNAMO_ACCOUNT_ID: {
      const aggregatedHours = row?.payroll?.aggregatedHours || {};
      const hourlyRate = 40;
      const actualTotalPayroll =
        hourlyRate * (Object.keys(aggregatedHours).reduce((carry, key) => carry + aggregatedHours?.[key] || 0, 0) || 0);
      const premia = actualTotalPayroll - (row?.payroll?.value || 0);
      const selectedPayrollValue = Math.max(row?.payroll?.value || 0, actualTotalPayroll);

      return {
        '40 NIS Calculation': {
          value: actualTotalPayroll,
          summarized: true
        },
        actualTotalPayroll: {
          value: selectedPayrollValue,
          summarized: true
        },
        premia: {
          value: premia > 0 ? premia : 0,
          summarized: true
        }
      };
    }
    case ETZ_HASADE_ACCOUNT_ID: {
      return {
        fullName: {
          value: `${employee?.firstName || ''} ${employee?.lastName || ''}`,
          summarized: false
        },
        idNumber: {
          value: employee?.extraFields?.find(({ id }) => id === 335)?.value || '',
          summarized: false
        }
      };
    }
    default:
      return {};
  }
};

export const addedColumnNamesPerAccount = ({ accountId }) => {
  switch (accountId) {
    case DYNAMO_ACCOUNT_ID:
      return ['actualTotalPayroll', 'premia', '40 NIS Calculation'];
    case ETZ_HASADE_ACCOUNT_ID:
      return ['fullName', 'idNumber'];
    default:
      return [];
  }
};

export const EXTRA_PAYROLL_COLUMNS = ['regular', 'overtime', 'overtime2'];

export const EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY = EXTRA_PAYROLL_COLUMNS.concat([
  'holidayRegular',
  'holidayOvertime',
  'holidayOvertime2'
]);

export const formatAggregatedHour = (value) =>
  value ? getDurationFromMilliseconds(getMillisecondsFromDecimal(value)) : '';

export const groupByProjectData = ({
  slots,
  groupByKey = 'date',
  employeesMap,
  search,
  sort,
  sortingColumns,
  mapPayroll = {},
  mappedClientBranches = {},
  byClient = false,
  summarized = true,
  hideOverbookingShifts = true
}) => {
  const formattedSlots = formatProjectDataSlots(slots, employeesMap, byClient);

  const filteredSlots = hideOverbookingShifts
    ? formattedSlots.filter(({ isOverbooking }) => !isOverbooking)
    : formattedSlots;

  const formattedData = filteredSlots.map((item) => {
    return {
      ...item,
      clientBranch: mappedClientBranches?.[item.clientBranchId],
      payroll: mapPayroll[item.timesheetId],
      actualTotal: !mapPayroll[item.timesheetId]?.aggregatedHours
        ? item.actualTotal
        : Object.values(mapPayroll[item.timesheetId]?.aggregatedHours || {}).reduce(
            (sum, value) => sum + getMillisecondsFromDecimal(value),
            0
          )
    };
  });

  const dataWithGroupKey = formattedData.map((item) => ({ ...item, groupKey: MAP_GROUP[groupByKey](item) }));
  const groups = groupBy(dataWithGroupKey, 'groupKey');

  const groupLists = Object.entries(groups).map(([groupValue, list]) => ({
    groupValue,
    shift: list.length,
    fields: {
      ...list.reduce((output, current) => {
        (current.payroll?.fields || []).forEach((field) => {
          output[field.id] = (output[field.id] || 0) + field.value;
          return output;
        });
        return output;
      }, {})
    },
    fieldsTotal: {
      ...list.reduce((output, current) => {
        (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
          output[field.id] = (output[field.id] || 0) + field.total;
          return output;
        });
        return output;
      }, {})
    },
    actualTotal: list.reduce((sum, { actualTotal }) => sum + actualTotal, 0),
    scheduleTotal: list.reduce((sum, { scheduleTotal }) => sum + scheduleTotal, 0),
    // clientWageTotal: list.reduce((sum, { clientWageTotal }) => sum + clientWageTotal, 0),
    difference: list.reduce((sum, { difference }) => sum + difference, 0),
    subRows: list,
    payrollTotal: list.reduce((sum, { payroll }) => sum + (payroll?.value || 0), 0)
  }));

  const allData = filterData(
    groupLists.reduce((all, { subRows }) => [...all, ...subRows], []),
    search
  );

  if (!summarized) {
    const groupedData = groupLists.map((groupedItem) => ({
      ...groupedItem,
      aggregatedHoursTotalRow: EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
        (prev, key) => ({
          ...prev,
          [key]: groupedItem.subRows.reduce((sum, subRow) => sum + (subRow?.payroll?.aggregatedHours?.[key] || 0), 0)
        }),
        {}
      )
    }));
    return groupedData;
  }

  if (sort) {
    allData.sort((a, b) =>
      sort.sortDirection === 'asc' ? sortingColumns?.[sort.sortBy]?.(a, b) : sortingColumns?.[sort.sortBy]?.(b, a)
    );
  }
  return [
    ...allData,
    {
      summarized: true,
      groupValue: '',
      totalRows: allData.length,
      actualTotal: allData.reduce((sum, { actualTotal }) => sum + actualTotal, 0),
      scheduleTotal: allData.reduce((sum, { scheduleTotal }) => sum + scheduleTotal, 0),
      difference: allData.reduce((sum, { difference }) => sum + difference, 0),
      payrollTotal: allData.reduce((sum, { payroll }) => sum + (payroll?.value || 0), 0),
      clientWageTotal: allData.reduce((sum, { payroll }) => sum + (payroll?.clientWageTotal || 0), 0),
      fields: {
        ...allData.reduce((output, current) => {
          (current.payroll?.fields || []).forEach((field) => {
            const value = (output[field.id] || 0) + field.value;
            output[field.id] = parseFloat(value);
            return output;
          });
          return output;
        }, {})
      },
      fieldsTotal: {
        ...allData.reduce((output, current) => {
          (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
            const totalValue = (output[field.id] || 0) + field.total;
            output[field.id] = parseFloat(totalValue);
            return output;
          });
          return output;
        }, {})
      },
      aggregatedHoursTotal: EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
        (prev, key) => ({
          ...prev,
          [key]: allData.reduce((sum, { payroll }) => sum + (payroll?.aggregatedHours?.[key] || 0), 0)
        }),
        {}
      )
    }
  ];
};

/* Get all fields and match their values for each user */
export const getValuesOfFields = (fields = [], accountFields, mappedFieldsValue) => {
  if (accountFields?.length === 0) return {};

  const employeeFieldsMapping = fields.reduce((output, current) => {
    const { id: fieldId, value } = current;
    if (!mappedFieldsValue[fieldId]) {
      return output;
    }

    /* convert numeric string values to number only if they don't start with '0' */
    const outputValue = isNaN(mappedFieldsValue[fieldId](value))
      ? mappedFieldsValue[fieldId](value)
      : typeof value === 'string' && value.startsWith('0')
      ? value
      : Number(mappedFieldsValue[fieldId](value));

    output[fieldId] = { ...current, value: outputValue };
    return output;
  }, {});

  return employeeFieldsMapping;
};

const PTO_TYPE_TO_SUMMARIZED_COLUMN = {
  [PTO_TYPES.SickDay]: SUMMARIZED_SICK_HOURS_COLUMN,
  [PTO_TYPES.Holiday]: SUMMARIZED_VACATION_HOURS_COLUMN
};

const getSummarizedLeaveColumn = (leaveType) =>
  LEAVE_TYPE_NAME_TO_SUMMARIZED_COLUMN[(leaveType || '').trim().toLowerCase()];

const getPayrollHours = (payrollEntry) =>
  payrollEntry?.aggregatedHours
    ? Object.values(payrollEntry.aggregatedHours).reduce((sum, val) => sum + (val || 0), 0)
    : 0;

// Leave that has not been finalized into payroll yet (wage type `notFinalizedLeave`) has no
// aggregatedHours, so fall back to the record's own duration the way the payroll grid does.
const getLeaveHours = (leaveRecord, payrollEntry) => {
  const payrollHours = getPayrollHours(payrollEntry);
  if (payrollHours > 0) {
    return payrollHours;
  }

  const durationHours = Number(leaveRecord?.durationHours);
  if (Number.isFinite(durationHours) && durationHours > 0) {
    return durationHours;
  }

  return totalTime(leaveRecord?.startTime, leaveRecord?.endTime) / (60 * 60 * 1000);
};

const groupByData = ({
  jobs,
  slots,
  pto = [],
  leaveRecords = [],
  employees,
  groupByKey,
  dateFormat,
  search,
  sort,
  sortingColumns,
  mappedLabels,
  mappedLocations,
  filters = {},
  mapPayroll = {},
  mapPayrollWithLabourCost = {},
  clientMapPayroll = {},
  mappedClientBranches = {},
  deductBreaktimesIsActive = false,
  byClient = false,
  filterDataFunction = null,
  mappedBranches = {},
  showOnlyUnapproved = false,
  summarized = true,
  addDummy = false
}) => {
  const data = groupByDataTimesheet({
    jobs,
    slots,
    pto,
    leaveRecords,
    employees,
    groupByKey,
    search,
    filters,
    dateFormat,
    filterDataFunction,
    byClient,
    sort,
    showOnlyUnapproved,
    deductBreaktimesIsActive,
    sortingColumns,
    mappedLabels,
    mappedLocations,
    addDummy,
    formatData: (formattedData) =>
      formattedData.map((item) => {
        const actualTotalInHours = !mapPayroll[item.timesheetId]?.aggregatedHours
          ? item.actualTotal / (60 * 60 * 1000)
          : Object.values(mapPayroll[item.timesheetId]?.aggregatedHours || {}).reduce((sum, value) => sum + value, 0);

        const vat = mappedBranches?.[item.branchId]?.payrollConfig?.vat;

        const clientWageTotal = clientMapPayroll?.[item.timesheetId]
          ? clientMapPayroll[item.timesheetId].value
          : (item.clientWage?.hourlyWage || 0) * actualTotalInHours;

        const payrollActualTotal = !mapPayroll[item.timesheetId]?.aggregatedHours
          ? item.actualTotal
          : Object.values(mapPayroll[item.timesheetId]?.aggregatedHours || {}).reduce(
              (sum, value) => sum + getMillisecondsFromDecimal(value),
              0
            );

        const payrollBreaksTotal = mapPayroll[item.timesheetId]?.breaktimes
          ? getMillisecondsFromDecimal(mapPayroll[item.timesheetId]?.breaktimes)
          : item.actualBreaksTotal;

        return {
          ...item,
          selectable: !!item?.adminApproved?.adminId || !!item?.timesheet?.adminApproved?.adminId,
          multipliers: mapPayroll?.[item.timesheetId]?.multipliers,
          clientBranch: mappedClientBranches?.[item.clientBranchId],
          payrollWithLabourCost: mapPayrollWithLabourCost[item.timesheetId],
          payroll: mapPayroll[item.timesheetId],
          clientPayroll: clientMapPayroll?.[item.timesheetId],
          payrollTotal: mapPayroll[item.timesheetId]?.value,
          labourCostTotal: mapPayrollWithLabourCost[item.timesheetId]?.value,
          payrollTotalWithVat: mapPayroll[item.timesheetId]?.value * (vat || 1),
          clientWageTotal,
          paidHours: mapPayroll[item.timesheetId]?.paidHours,
          breakWarnings: mapPayroll[item.timesheetId]?.breakWarnings,
          actualTotalWithoutBreaks: payrollActualTotal + (deductBreaktimesIsActive ? payrollBreaksTotal : 0), // net is the same if we dont deduct breaktimes, otherwise we need to add breaks to net
          actualTotal: payrollActualTotal
        };
      })
  });

  const allData = data.reduce((all, { subRows }) => {
    all.push(...subRows);
    return all;
  }, []);

  const groupedData = data.map((groupedItem) => ({
    ...groupedItem,
    aggregatedHoursTotalRow: EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
      (prev, key) => ({
        ...prev,
        [key]: groupedItem.subRows.reduce((sum, subRow) => sum + (subRow?.payroll?.aggregatedHours?.[key] || 0), 0)
      }),
      {}
    )
  }));

  if (!summarized) {
    return groupedData;
  }

  groupedData.push({
    summarized: true,
    groupValue: '',
    totalRows: groupByKey === TIMESHEET_GROUP_BY_OPTIONS_ENUM.NO_GROUP_BY ? allData.length : groupedData.length,
    shift: allData.length,
    actualTotal: allData.reduce((sum, { actualTotal }) => sum + actualTotal, 0),
    scheduleTotal: allData.reduce((sum, { scheduleTotal }) => sum + scheduleTotal, 0),
    clientWageTotal: allData.reduce((sum, { clientWageTotal }) => sum + clientWageTotal, 0),
    difference: allData.reduce((sum, { difference }) => sum + difference, 0),
    payrollTotal: allData.reduce((sum, { payroll }) => sum + (payroll?.value || 0), 0),
    labourCostTotal: allData.reduce((sum, { payrollWithLabourCost }) => sum + (payrollWithLabourCost?.value || 0), 0),
    breaktimes: allData.reduce((all, { breaktimes }) => {
      if (breaktimes) {
        all.push(...breaktimes);
      }
      return all;
    }, []),
    fields: {
      ...allData.reduce((output, current) => {
        (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
          const value = (output[field.id] || 0) + field.value;
          output[field.id] = parseFloat(value);
          return output;
        });
        return output;
      }, {})
    },
    accountFields: allData.reduce((output, current) => {
      (current.employee?.fields || []).forEach((field) => {
        output[field.id] = field?.value;
        return output;
      });
      return output;
    }, {}),
    fieldsTotal: {
      ...allData.reduce((output, current) => {
        (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
          const totalValue = (output[field.id] || 0) + field.total;
          output[field.id] = parseFloat(totalValue);
          return output;
        });
        return output;
      }, {})
    },
    aggregatedHoursTotal: EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
      (prev, key) => ({
        ...prev,
        [key]: allData.reduce((sum, { payroll }) => sum + (payroll?.aggregatedHours?.[key] || 0), 0)
      }),
      {}
    )
  });

  return groupedData;
};

export default groupByData;

export const filterPayrollFieldsByDate = (fields, startTime) =>
  startTime
    ? fields.filter(({ deletedAt }) => !deletedAt || moment(deletedAt, API_DATE_TIME_FORMAT).isAfter(startTime))
    : [];

export const generateCSV = ({
  columns,
  t,
  from,
  to,
  timesheetData,
  mapPayroll,
  clientMapPayroll,
  mapPayrollWithLabourCost,
  mappedPositions,
  mappedJobTypes,
  mappedTags,
  filterDataFunction,
  mappedAdmins,
  mappedLabels,
  mappedLocations,
  mappedBranches,
  mappedClientBranches,
  detailedWithSummaries,
  fields,
  accountFields,
  mappedFieldsValue,
  search,
  accountName,
  filters,
  handleError,
  firstDayOfWeek,
  dateFormat,
  timeFormat,
  formattingMap,
  exportTitle,
  accountId,
  rowTransformer,
  addDummy = false,
  isPayRatesEnabled = false,
  showHolidayColumns = true,
  showPoNumber = false,
  summarized = false,
  showBranchName = false,
  basePosition = false,
  groupByWage = false,
  salarySystem = false,
  showLabourCostColumn = false,
  timesheetFields = [],
  premiumEventStaffExport = false,
  mappedPtoTypes = {}
}) => {
  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    pto: timesheetData?.pto,
    leaveRecords: timesheetData?.leave,
    employees: timesheetData?.employees,
    filterDataFunction,
    groupByKey: 'employee',
    mapPayroll,
    clientMapPayroll,
    mapPayrollWithLabourCost,
    search,
    filters,
    summarized: false,
    addDummy
  });
  // Sick/vacation hours are fed by both PTO records and contract-leave records.
  const timeOffHoursByEmployee = {};

  const addTimeOffHours = ({ employeeId, column, hours }) => {
    if (!employeeId || !column || !(hours > 0)) return;

    if (!timeOffHoursByEmployee[employeeId]) {
      timeOffHoursByEmployee[employeeId] = {
        [SUMMARIZED_SICK_HOURS_COLUMN]: 0,
        [SUMMARIZED_VACATION_HOURS_COLUMN]: 0
      };
    }

    timeOffHoursByEmployee[employeeId][column] += hours;
  };

  (timesheetData?.pto || []).forEach((pto) => {
    const { employeeId, type, timesheetId } = pto || {};
    if (!timesheetId) return;

    addTimeOffHours({
      employeeId,
      column: PTO_TYPE_TO_SUMMARIZED_COLUMN[type],
      hours: getPayrollHours(mapPayroll[timesheetId])
    });
  });

  (timesheetData?.leave || []).forEach((leaveRecord) => {
    const { employeeId, leaveType, timesheetId, timesheet } = leaveRecord || {};

    addTimeOffHours({
      employeeId,
      column: getSummarizedLeaveColumn(leaveType),
      hours: getLeaveHours(leaveRecord, mapPayroll[timesheetId || timesheet?.id])
    });
  });

  // Check if there are any time off hours to show
  const hasTimeOffData = Object.values(timeOffHoursByEmployee).some(
    (timeOff) => timeOff[SUMMARIZED_SICK_HOURS_COLUMN] > 0 || timeOff[SUMMARIZED_VACATION_HOURS_COLUMN] > 0
  );

  if (groupedData?.length <= 0 && handleError) {
    handleError(t('noDataToExport'));
    return;
  }

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  const dateSorter = SORT_BY_DATE('date');
  const sorter = (a, b) => SORT_BY_NAME(a, b) || dateSorter(a, b);

  if (detailedWithSummaries) {
    if (accountId === YORDEI_HASIRA_ACCOUNT_ID) {
      return generalPayrollDetailedWithSummariesHasira({
        groupedData,
        mappedTags,
        employeesMap,
        firstDayOfWeek,
        t,
        timeFormat,
        dateFormat,
        dateSorter,
        mappedPositions,
        accountName
      });
    }

    return generalPayrollDetailedWithSummaries({
      groupedData,
      mappedTags,
      employeesMap,
      firstDayOfWeek,
      t,
      timeFormat,
      dateFormat,
      dateSorter,
      mappedPositions,
      mappedLocations,
      accountName,
      timesheetFields
    });
  }

  if (salarySystem) {
    return generalPayrollSalarySystem({
      groupedData,
      columns,
      t,
      from,
      to,
      accountName
    });
  }

  if (groupByWage) {
    return generalPayrollGroupByWage({
      groupedData,
      columns,
      mappedFieldsValue,
      employeesMap,
      accountFields,
      t,
      from,
      to,
      accountId,
      accountName
    });
  }

  if (basePosition) {
    return generalPayrollGroupByPosition({
      groupedData,
      columns,
      t,
      from,
      to,
      accountId,
      mappedPositions,
      accountName
    });
  }

  if (premiumEventStaffExport) {
    return generalPremiumEventStaff({
      groupedData,
      formattingMap,
      columns,
      t,
      from,
      to,
      dateFormat,
      mappedTags,
      exportTitle
    });
  }

  const visibleData = summarized
    ? groupedData.map((data) => {
        const {
          groupValue,
          shift,
          actualTotal,
          payrollTotal,
          labourCostTotal,
          clientWageTotal,
          aggregatedHoursTotalRow,
          subRows,
          fields: rowFields,
          fieldsTotal
        } = data;

        const { totalBreaks, additionalColumns } = subRows.reduce(
          ({ totalBreaks: oldTotalBreaks, additionalColumns: oldAdditionalColumns }, row) => {
            const { breaktimes = [] } = row;

            const newTotalBreaks =
              oldTotalBreaks +
              breaktimes.reduce((sum, { startTime, endTime }) => sum + totalTime(startTime, endTime), 0);

            const additionalColumnsSubRow = addedColumnsPerAccount({
              accountId,
              row,
              employee: employeesMap[groupValue]
            });

            const newAdditionalColumns = Object.keys(additionalColumnsSubRow).reduce(
              (carry, key) => ({
                ...carry,
                [key]: additionalColumnsSubRow?.[key].summarized
                  ? (oldAdditionalColumns?.[key] || 0) + (additionalColumnsSubRow?.[key]?.value || 0)
                  : additionalColumnsSubRow?.[key]?.value
              }),
              {}
            );

            return {
              totalBreaks: newTotalBreaks,
              additionalColumns: newAdditionalColumns
            };
          },
          { totalBreaks: 0, additionalColumns: {} }
        );

        // Get sick/vacation hours from pre-calculated payroll data
        const timeOffHours = hasTimeOffData
          ? timeOffHoursByEmployee[groupValue] || {
              [SUMMARIZED_SICK_HOURS_COLUMN]: 0,
              [SUMMARIZED_VACATION_HOURS_COLUMN]: 0
            }
          : null;

        const employeeFieldsMapping = getValuesOfFields(
          employeesMap[groupValue]?.fields,
          accountFields,
          mappedFieldsValue
        );

        const employeeFields = (accountFields || []).reduce((output, { id, name }) => {
          output[name] = employeeFieldsMapping[id]?.value || '';
          return output;
        }, {});

        const employeeID = isNaN(employeesMap[groupValue].identification)
          ? employeesMap[groupValue].identification
          : Number(employeesMap[groupValue].identification);

        const result = {
          'employee-id': employeeID || '',
          firstName: employeesMap[groupValue].firstName,
          lastName: employeesMap[groupValue].lastName,
          numOfShifts: shift,
          totalHours: actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(2)) : '',
          hourlyRate: subRows?.[0].wage?.hourlyWage || 0,
          flatWage: subRows?.[0].wage?.flatWage || 0,
          flatHours: subRows?.[0].wage?.flatHours || 0,
          clientWage: subRows?.[0].clientWage?.hourlyWage || 0,
          ...(showHolidayColumns ? EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY : EXTRA_PAYROLL_COLUMNS).reduce(
            (prev, key) => ({ ...prev, [key]: aggregatedHoursTotalRow?.[key] || '' }),
            {}
          ),
          ...(fields || []).reduce((output, current, index) => {
            const slug = current.slug || `other ${index}`;
            return { ...output, [slug]: rowFields[current.id] || '' };
          }, {}),
          ...(fields || []).reduce((output, current, index) => {
            const slug = `${current.slug} ${t('total')}` || `other ${index}`;
            return { ...output, [slug]: fieldsTotal[current.id] || '' };
          }, {}),
          // ...(subRows?.[0]?.employee?.fields || []).reduce((output, { name, value }) => {
          //   output[name] = value;
          //   return output;
          // }, {}),
          phone: employeesMap[groupValue]?.phone
            ? parsePhoneNumberFromString(`+${employeesMap[groupValue].phone}`)?.formatNational?.()
            : '',
          email: employeesMap[groupValue]?.email || '',
          ...employeeFields,
          totalPayroll: payrollTotal || '',
          clientWageTotal: clientWageTotal || '',
          ...(showLabourCostColumn ? { labourCost: labourCostTotal || '' } : {}),
          breaks: totalBreaks > 0 ? getDurationFromMilliseconds(totalBreaks) : '',
          ...additionalColumns,
          ...(hasTimeOffData && timeOffHours
            ? {
                [SUMMARIZED_SICK_HOURS_COLUMN]: timeOffHours[SUMMARIZED_SICK_HOURS_COLUMN] || '',
                [SUMMARIZED_VACATION_HOURS_COLUMN]: timeOffHours[SUMMARIZED_VACATION_HOURS_COLUMN] || ''
              }
            : {})
        };

        return conditionalColumns(rowTransformer ? rowTransformer(result, data) : result, columns);
      })
    : groupedData
        .reduce((prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))], [])
        .sort(sorter)
        .map((row) => {
          const addedColumns = addedColumnsPerAccount({ accountId, row, employee: employeesMap[row.groupValue] });
          const approvedByAdminData = row?.adminApproved?.adminId ? mappedAdmins[row.adminApproved.adminId] : null;

          const shiftTime =
            row.scheduleOut && moment(row.scheduleOut).isValid()
              ? `${moment(row.scheduleIn).format(timeFormat)} - ${moment(row.scheduleOut).format(timeFormat)}`
              : `${moment(row.scheduleIn).format(timeFormat)} `;

          const booker = mappedAdmins[row?.bookerId] || null;

          const employeeFieldsMapping = getValuesOfFields(row?.employee?.fields, accountFields, mappedFieldsValue);

          const employeeFields = (accountFields || []).reduce((output, { id, name }) => {
            output[name] = employeeFieldsMapping[id]?.value || '';
            return output;
          }, {});

          let formattedBreaktimes = '';
          let totalDurationOfBreaks = '';

          if (row?.breaktimes && row.breaktimes.length > 0) {
            formattedBreaktimes = row.breaktimes
              .map(({ startTime, endTime }) => {
                const formattedStartTime = moment(startTime).format(timeFormat);
                const formattedEndTime =
                  endTime && moment(endTime).isValid() ? moment(endTime).format(timeFormat) : null;
                return `${formattedStartTime} - ${formattedEndTime || t('onBreak')}`;
              })
              .join(',');

            const mappedBreaksToDuration = row?.breaktimes
              ?.filter(
                ({ startTime, endTime }) =>
                  startTime && moment(startTime).isValid() && endTime && moment(endTime).isValid()
              )
              .map(({ startTime, endTime }) => totalTime(startTime, endTime))
              .reduce((all, duration) => all + duration, 0);

            totalDurationOfBreaks =
              mappedBreaksToDuration === 0 ? '' : getDurationFromMilliseconds(mappedBreaksToDuration);
          }

          const employeeID = isNaN(row.employee.identification)
            ? row.employee.identification
            : Number(row.employee.identification);

          const date = row.shiftDate || row.date;

          let hourlyRate = row.wage?.hourlyWage || 0;
          let flatWage = row.wage?.flatWage || 0;
          let flatHours = row.wage?.flatHours || 0;
          let clientWage = row.clientWage?.hourlyWage || 0;
          let clientFlatWage = undefined;
          if (isPayRatesEnabled) {
            const { payroll, clientPayroll } = row || {};
            const { wage: wagePayroll } = payroll || {};
            const { wage: clientWagePayroll } = clientPayroll || {};

            hourlyRate = (wagePayroll || [])
              .map((current) => `${current.hourlyWage} (${current.tag?.name})`)
              .join(', ');
            flatHours = (wagePayroll || []).map((current) => `${current.flatWage} (${current.tag?.name})`).join(', ');
            clientWage = (clientWagePayroll || [])
              .map((current) => `${current.hourlyWage} (${current.tag?.name})`)
              .join(', ');
            clientFlatWage = (clientWagePayroll || [])
              .map((current) => `${current.flatWage} (${current.tag?.name})`)
              .join(', ');
          }

          let payrollProcessStatus = '';
          let payrollProcessCreatedBy = '';
          let payrollProcessCreatedAt = '';
          if (row?.payrollProcess) {
            payrollProcessStatus = row.payrollProcess.status === 'processed' ? 'Processed' : 'Pay query' || '';
            payrollProcessCreatedAt =
              moment.utc(row.payrollProcess.createdAt).local().format(API_DATE_TIME_FORMAT) || '';
            payrollProcessCreatedBy = [
              mappedAdmins?.[row?.payrollProcess.createdBy]?.firstName,
              mappedAdmins?.[row?.payrollProcess.createdBy]?.lastName
            ]
              ?.filter((val) => !!val)
              ?.join(' ');
          }

          const result = {
            'employee-id': employeeID || '',
            approvalStatus: row.approved ? t('approved') : t('notApproved'),
            clientStatus: row.clientApproved ? t('yes') : t('no'),
            approvedBy: approvedByAdminData ? `${approvedByAdminData.firstName} ${approvedByAdminData.lastName}` : '',
            approvedAt: row?.adminApproved?.createdAt
              ? moment(row.adminApproved.createdAt).format(API_DATE_TIME_FORMAT)
              : '',
            columnHeaderProcessedPayroll: payrollProcessStatus,
            processedAtColumnHeader: payrollProcessCreatedAt,
            processedByColumnHeader: payrollProcessCreatedBy,
            clientName: row?.clientBranchId ? mappedClientBranches?.[row?.clientBranchId]?.name || '' : '',
            firstName: row.employee.firstName,
            lastName: row.employee.lastName,
            date: moment(date, API_DATE_TIME_FORMAT).format(dateFormat),
            dayOfWeek: moment(date, API_DATE_TIME_FORMAT).format('ddd'),
            shiftTime,
            position: mappedPositions[row.positionId]?.label || '',
            positionGroups:
              (mappedPositions?.[row.positionId]?.positionGroups || []).map(({ name }) => name).join(', ') || '',
            shift: generateShiftName(row, mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes),
            shiftNotes: row.comments || '',
            tags: row.tags ? row.tags.map(({ id }) => mappedTags[id]?.label).join(', ') : '',
            eventName: row.name || '',
            mainEventName: row.mainProjectName || '',
            mainEventIdentifier: row.mainProjectIdentifier || '',
            mainEventGlCode: row.mainProjectGlCode || '',
            mainEventDoorTime: row.mainProjectDoorTime
              ? moment(row.mainProjectDoorTime, 'HH:mm').format(timeFormat)
              : '',
            mainEventEstimatedAttendance: row.mainProjectEstimatedAttendance ?? '',
            mainEventExpectedRevenue: row.mainProjectExpectedRevenue ?? '',
            mainEventTotalActualRevenue: row.mainProjectTotalActualRevenue ?? '',
            mainEventActualAttendance: row.mainProjectActualAttendance ?? '',
            ...(showBranchName ? { branchName: mappedBranches?.[row.branchId]?.name || '' } : {}),
            in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
            out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
            checkIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
            checkOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
            location: row.locationId ? mappedLocations[row.locationId]?.name : '',
            address: row.locationId ? mappedLocations[row.locationId]?.address || '' : '',
            ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
              (prev, key) => ({ ...prev, [key]: row.payroll?.aggregatedHours?.[key] || '' }),
              {}
            ),
            eventBooker: booker ? `${booker?.firstName} ${booker?.lastName}` : '',
            breaks: formattedBreaktimes,
            totalBreakHours: totalDurationOfBreaks
              ? Number(moment.duration(totalDurationOfBreaks).asHours().toFixed(2))
              : '',
            totalHours: row.actualTotal ? Number(moment.duration(row.actualTotal).asHours().toFixed(2)) : '',
            hourlyRate,
            flatWage,
            flatHours,
            clientWage,
            clientFlatWage,
            ...(fields || []).reduce((output, current, index) => {
              const slug = current.slug || `other ${index}`;
              return {
                ...output,
                [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.value || ''
              };
            }, {}),
            ...(fields || []).reduce((output, current, index) => {
              const slug = `${current.slug} ${t('total')}` || `other ${index}`;
              return {
                ...output,
                [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.total || ''
              };
            }, {}),
            phone: row.employee?.phone ? parsePhoneNumberFromString(`+${row.employee?.phone}`)?.formatNational?.() : '',
            email: row.employee?.email || '',
            ...employeeFields,
            totalPayroll: row.payroll?.value ? Number(row.payroll.value.toFixed(2)) : '',
            clientWageTotal: row.clientWageTotal,
            ...(showLabourCostColumn
              ? {
                  labourCost: row.payrollWithLabourCost?.value ? Number(row.payrollWithLabourCost.value.toFixed(2)) : ''
                }
              : {}),
            ...Object.keys(addedColumns).reduce((carry, key) => ({ ...carry, [key]: addedColumns?.[key]?.value }), {}),
            ...(showPoNumber ? { poNumber: row.poNumber || '' } : {}),
            invoiceNumber: row.invoiceNumber || '',
            payrollExportInvoiceStatus: Number.isInteger(row?.invoiceStatus)
              ? row?.invoiceStatus === 0
                ? t('notIssued')
                : t('issued')
              : '',
            payrollNotes: row.payrollNotes,
            rating: row.rating || '',
            schedulingNotes: row.schedulingNotes,
            costCenter: row?.costCenter
              ? `${row.costCenter.name || ''}${row.costCenter.identifier ? ` (${row.costCenter.identifier})` : ''}`
              : '',
            clientCostCenter: row?.clientCostCenter
              ? `${row.clientCostCenter.name || ''}${
                  row.clientCostCenter.identifier ? ` (${row.clientCostCenter.identifier})` : ''
                }`
              : '',
            // Timesheet fields
            ...getTimesheetFieldsForExport({ row, timesheetFields, t, dateFormat })
          };

          return conditionalColumns(rowTransformer ? rowTransformer(result, row) : result, columns);
        });

  const summaryColumns = [
    ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.map((key) => t(key)),
    ...(fields || [])
      .filter(
        ({ type }) =>
          ![PAYROLL_CUSTOM_FIELD_TYPE.HOURLY_WAGE_PERCENTAGE, PAYROLL_CUSTOM_FIELD_TYPE.HOURS_RELATED].includes(type)
      )
      .map(({ slug }) => t(slug)),
    ...(fields || []).map(({ slug }) => `${t(slug)} ${t('total')}`),
    t('totalTime'),
    t('totalPayroll'),
    t('clientWageTotal'),
    ...(showLabourCostColumn ? [t('labourCost')] : [])
  ].filter((slug) => columns.some(({ Header: columnHeader }) => columnHeader === slug));

  exportExcel({
    baseData: visibleData || [],
    name: [accountName, t(summarized ? 'summarizedPayrollReport' : 'detailedPayrollReport'), from, to].join(' - '),
    showTitle: false,
    summaryColumns
  });
};

const roundTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const getWeekEndingDate = ({ date, firstDayOfWeek, dateFormat }) => {
  if (!date || !firstDayOfWeek) {
    return '';
  }

  const shiftMoment = moment(date, API_DATE_TIME_FORMAT);
  if (!shiftMoment.isValid()) {
    return '';
  }

  const lastDayOfWeek = (firstDayOfWeek + 5) % 7;
  const daysUntilWeekEnd = (lastDayOfWeek - shiftMoment.day() + 7) % 7;
  return shiftMoment.add(daysUntilWeekEnd, 'days').format(dateFormat);
};

export const generateTwentyFourSevenPayrollExport = async ({
  timesheetData,
  mapPayroll,
  timeRange,
  search,
  filters,
  firstDayOfWeek,
  dateFormat
}) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const rowsByEventAndEmployee = new Map();

  groupedData
    .flatMap(({ subRows = [] }) => subRows)
    .forEach(({ isJob, isPto, isLeave, payroll, approved, projectId, employee, shiftDate }) => {
      if (isJob || isPto || isLeave || !payroll || !approved) {
        return;
      }

      const aggregatedHours = payroll.aggregatedHours || {};
      const regular = roundTwo(aggregatedHours.regular ?? 0);
      const overtime = roundTwo(
        (aggregatedHours.overtime ?? 0) +
          (aggregatedHours.overtime2 ?? 0) +
          (aggregatedHours.holidayOvertime ?? 0) +
          (aggregatedHours.holidayOvertime2 ?? 0)
      );

      const eventId = projectId ?? 0;
      const employeeId = employee?.id ?? 0;
      const key = `${eventId}_${employeeId}`;
      const existing = rowsByEventAndEmployee.get(key);

      if (existing) {
        existing.Reg = roundTwo(existing.Reg + regular);
        existing.OT = roundTwo(existing.OT + overtime);
        return;
      }

      rowsByEventAndEmployee.set(key, {
        Placement: `${eventId}${employeeId}`,
        Name: `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim(),
        'Week ending': getWeekEndingDate({ date: shiftDate, firstDayOfWeek, dateFormat }),
        Reg: regular,
        OT: overtime
      });
    });

  const name = `24Seven Payroll ${timeRange.start.format('DD-MM-YYYY')} - ${timeRange.end.format('DD-MM-YYYY')}`;

  const { buffer, fileName } = await exportExcel({
    baseData: Array.from(rowsByEventAndEmployee.values()),
    name,
    showTitle: false,
    autoFilter: false,
    showColumnsRowStyle: false,
    asCSV: true,
    isDownload: false
  });

  saveAs(new Blob([buffer], { type: 'text/csv;charset=utf-8' }), fileName);
};

export const generatePayrollSystem = ({
  t,
  timesheetData,
  mapPayroll,
  timeRange,
  accountId,
  fields,
  search,
  filters,
  accountName
}) => {
  if (!timeRange) {
    return;
  }
  const month = parseInt(timeRange.start.format('M'), 10);
  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  if (!SHIKLULIT?.[accountId] && !MICHPAL?.[accountId]) {
    // eslint-disable-next-line no-console
    console.error(`The report is not configured for account ${accountId}`);
    return;
  }

  // Make a map of all employees fields
  const employeesFields = timesheetData?.employees?.reduce(
    (carry, { id: employeeId, fields: employeeFields, extraFields, identification }) => {
      carry.set(employeeId, {
        31: identification,
        ...(extraFields || []).reduce((prevCarry, item) => ({ ...prevCarry, [item.id]: item.value }), {}),
        ...(employeeFields || []).reduce((prevCarry, item) => ({ ...prevCarry, [item.id]: item.value }), {})
      });

      return carry;
    },
    new Map()
  );

  if (MICHPAL?.[accountId]) {
    return MichpalCSV({
      t,
      companyData: MICHPAL?.[accountId],
      mapPayroll,
      groupedData,
      accountId,
      employeesFields,
      employeesMap,
      fields,
      timeRange,
      month
    });
  }

  const accountsWithEmployeeId = [144];

  const data = [];
  const summaryData = [];

  const mapPayrollByEmployee = Object.values(mapPayroll).reduce((all, { employeeId, value }) => {
    if (!all[employeeId]) {
      all[employeeId] = 0;
    }

    all[employeeId] += value;
    return all;
  }, {});

  groupedData.forEach((item) => {
    const employeeId = parseInt(item.groupValue, 10);

    const employeeNumberField = item.subRows[0]?.employee.identification;

    // only for accountId = 144 - תעודת זהות
    const employeeIdField = accountsWithEmployeeId.includes(accountId)
      ? parseInt(employeesFields.get(employeeId)?.[accountId === 144 ? 297 : 4848], 10)
      : '';

    const isFriendsAccount = accountId === FRIENDS_ACCOUNT_ID;
    const rowContexts = isFriendsAccount ? buildFriendsShiklulitRowContextsForEmployee(item) : [item];

    const buildShiklulitRow = (type, code, priceColumn, qtyColumn) => ({
      חודש: month,
      'מספר עובד': employeeNumberField || '',
      ...(accountsWithEmployeeId.includes(accountId) && { 'תעודת זהות': employeeIdField || '' }),
      'סוג רשומה': type,
      'קוד רכיב': code,
      תעריף: priceColumn,
      כמות: !isFriendsAccount && priceColumn === 0 ? 0 : qtyColumn
    });

    if (isFriendsAccount && rowContexts.length > 1) {
      buildFriendsShiklulitDataRows({
        item,
        rowContexts,
        accountId
      }).forEach(({ type, code, priceColumn, qtyColumn }) =>
        data.push(buildShiklulitRow(type, code, priceColumn, qtyColumn))
      );
    } else {
      rowContexts.forEach((rowItem) => {
        const employeeData = [];

        SHIKLULIT?.[accountId]?.forEach(({ type, code, price, qty }) => {
          const priceColumn = typeof price === 'function' ? price(rowItem, employeeData) : price;
          const qtyColumn = typeof qty === 'function' ? qty(rowItem) : qty;

          data.push(buildShiklulitRow(type, code, priceColumn, qtyColumn));

          employeeData.push({ price: priceColumn, qty: qtyColumn });
        });
      });
    }

    rowContexts.forEach((rowItem) => {
      const employeeData = [];
      let contextShiklulitTotal = 0;

      SHIKLULIT?.[accountId]?.forEach(({ type, price, qty }) => {
        const priceColumn = typeof price === 'function' ? price(rowItem, employeeData) : price;
        const qtyColumn = typeof qty === 'function' ? qty(rowItem) : qty;

        if (type !== 4) {
          contextShiklulitTotal += (priceColumn || 0) * (qtyColumn || 0);
        }

        employeeData.push({ price: priceColumn, qty: qtyColumn });
      });

      const ubeyaTotalForContext = isFriendsAccount
        ? rowItem.subRows.reduce(
            (sum, sr) =>
              sum +
              (mapPayroll[sr.timesheetId]?.value !== undefined && mapPayroll[sr.timesheetId]?.value !== null
                ? mapPayroll[sr.timesheetId].value
                : sr.payroll?.value || 0),
            0
          )
        : mapPayrollByEmployee[employeeId];

      summaryData.push({
        חודש: month,
        'מספר עובד': employeeNumberField || '',
        ...(accountsWithEmployeeId.includes(accountId) && { 'תעודת זהות': employeeIdField || '' }),
        'סך הכל': contextShiklulitTotal,
        'סך הכל אובייה': ubeyaTotalForContext
      });
    });
  });

  exportExcel({
    baseData: data,
    name: [accountName, t('systemPayroll')].join(' - '),
    title: '',
    detailedTable: false,
    showTitle: false,
    addExtraSheet: true,
    extraSheetTitle: t('summary'),
    extraSheetData: summaryData,
    autoFilter: false
  });
};

export const generatePayrollExportSAP = ({ timesheetData, mapPayroll, timeRange, search, filters }) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const data = [];

  const MAP_DAYS_LABEL_TRANSLATION = {
    0: 'Zondag',
    1: 'Maandag',
    2: 'Dinsdag',
    3: 'Woensdag',
    4: 'Donderdag',
    5: 'Vrijdag',
    6: 'Zaterdag'
  };

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  const days = moment(timeRange.end).diff(moment(timeRange.start), 'days') + 1;
  const start = moment(timeRange.start).add(-1, 'day');

  const allDaysInRange = [...Array(days)].map((_) => {
    const date = moment(start.add(1, 'day'));
    const formattedDate = date.format(API_DATE_FORMAT);

    return {
      formattedDate,
      label: `${MAP_DAYS_LABEL_TRANSLATION[date.day()]}`
    };
  });

  const hoursBucketByEmployeeDate = {};
  const showDateNextToLabel = allDaysInRange.length > 7;

  groupedData.forEach(({ subRows = [], groupValue }) => {
    const employeeId = parseInt(groupValue, 10);

    if (!hoursBucketByEmployeeDate[employeeId]) {
      hoursBucketByEmployeeDate[employeeId] = {};
    }
    subRows.forEach(({ shiftDate, payroll }) => {
      if (!payroll || !payroll.hours) {
        return;
      }

      const dayHours = (payroll.hours || []).find(({ slug }) => slug === 'DR')?.value || 0;
      const nightHours = (payroll.hours || []).find(({ slug }) => slug === 'DOT1')?.value || 0;

      if (!hoursBucketByEmployeeDate[employeeId][shiftDate]) {
        hoursBucketByEmployeeDate[employeeId][shiftDate] = { dayHours: 0, nightHours: 0 };
      }
      hoursBucketByEmployeeDate[employeeId][shiftDate].dayHours += dayHours;
      hoursBucketByEmployeeDate[employeeId][shiftDate].nightHours += nightHours;
    });
  });

  groupedData.forEach((item) => {
    const employeeId = parseInt(item.groupValue, 10);

    const costCenterField = (employeesMap[employeeId]?.fields || []).find(({ id }) => id === 27619)?.value || '';
    const contractField = (employeesMap[employeeId]?.fields || []).find(({ id }) => id === 27516)?.value || '';

    const employeeNumberField = item.subRows[0]?.employee?.identification;
    const date = item.subRows[0]?.date;
    const currentEmplyeHoursBucket = hoursBucketByEmployeeDate[employeeId];

    const hoursParams = {
      //Keep empty
      Uitbetalen: '',
      //Keep empty
      Doorbetalen: ''
    };

    const employeeParams = {
      // employeeId
      PersoneelsNummer: employeeNumberField,
      //Keep empty
      TypeMedewerker: '',
      // Name
      Naam: [employeesMap[employeeId].firstName, employeesMap[employeeId].lastName].filter((curr) => !!curr).join(' '),
      //Payroll week
      Week: moment(date, API_DATE_FORMAT).week(),
      //Payroll year
      Jaar: moment(date, API_DATE_FORMAT).year(),
      //fieldId = 27516
      ContractUren: contractField,
      // Cost center field 27619
      Kostenplaats: costCenterField
    };

    data.push({
      ...employeeParams,
      //HNOR = normal hours, T135 = Night hours, Ziek = Sick
      Code: 'HNOR',
      ...hoursParams,
      //mondday
      ...Object.fromEntries(
        allDaysInRange.map(({ label, formattedDate }) => [
          showDateNextToLabel ? `${label} (${formattedDate})` : label,
          currentEmplyeHoursBucket?.[formattedDate]?.dayHours || 0
        ])
      ),
      //Keep empty
      '402M': ''
    });

    data.push({
      ...employeeParams,
      //HNOR = normal hours, T135 = Night hours, Ziek = Sick
      Code: 'T135',
      ...hoursParams,
      //mondday
      ...Object.fromEntries(
        allDaysInRange.map(({ label, formattedDate }) => [
          showDateNextToLabel ? `${label} (${formattedDate})` : label,
          currentEmplyeHoursBucket?.[formattedDate]?.nightHours || 0
        ])
      ),

      //Keep empty
      '402M': ''
    });
  });

  exportExcel({
    baseData: data,
    name: 'Customized payroll',
    title: ''
  });
};

export const generateExportToPayrollJCAToSAP = ({
  timesheetData,
  mapPayroll,
  timeRange,
  search,
  filters,
  positionsGroups = [],
  asCSV = false
}) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const data = [];

  const MAP_DAYS_LABEL_TRANSLATION = {
    0: 'Zondag',
    1: 'Maandag',
    2: 'Dinsdag',
    3: 'Woensdag',
    4: 'Donderdag',
    5: 'Vrijdag',
    6: 'Zaterdag'
  };

  //group by pay code
  // const positionsToCodes = {
  //   22742: 'H300',
  //   22733: 'H300',
  //   23267: 'H300',
  //   22734: 'H300',
  //   22739: 'H300',
  //   22737: 'H300',
  //   22732: 'H300',
  //   22735: 'H300',
  //   22743: 'H300',
  //   22741: 'H300',
  //   22744: 'H300',
  //   22745: 'H300',
  //   22746: 'H300',
  //   22747: 'H300',
  //   22749: 'H300',
  //   22751: 'H300',
  //   22753: 'H300',
  //   22754: 'H300',
  //   22755: 'H300',
  //   23268: 'H300',

  //   22740: 'H403',
  //   23374: 'H403',
  //   23213: 'H403',
  //   22752: 'H403',
  //   23212: 'H403',
  //   23373: 'H403',
  //   23488: 'H403',
  //   23489: 'H403',

  //   22738: 'H507',
  //   22750: 'H507',

  //   22736: 'HB04',
  //   23208: 'HB04',
  //   22748: 'HB04',
  //   23207: 'HB04'
  // };

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  const days = moment(timeRange.end).diff(moment(timeRange.start), 'days') + 1;
  const start = moment(timeRange.start).add(-1, 'day');

  const allDaysInRange = [...Array(days)].map((_) => {
    const date = moment(start.add(1, 'day'));
    const formattedDate = date.format(API_DATE_FORMAT);

    return {
      formattedDate,
      label: `${MAP_DAYS_LABEL_TRANSLATION[date.day()]}`
    };
  });

  const hoursBucketByEmployeeDate = {};
  const groupedByEmployeePosition = {};

  const showDateNextToLabel = allDaysInRange.length > 7;

  const positionGroupByPositionId = positionsGroups.reduce((output, current) => {
    (current.positionsIds || []).forEach((positionId) => {
      output[positionId] = { id: current.id, name: current.name };
    });

    return output;
  }, {});

  const mappedPositionGroups = positionsGroups.reduce((output, current) => {
    output[current.id] = current;

    return output;
  }, {});

  groupedData.forEach(({ subRows = [], groupValue }) => {
    const employeeId = parseInt(groupValue, 10);

    if (!hoursBucketByEmployeeDate[employeeId]) {
      hoursBucketByEmployeeDate[employeeId] = {};
    }

    if (!groupedByEmployeePosition[employeeId]) {
      groupedByEmployeePosition[employeeId] = {};
    }

    subRows.forEach((subRow) => {
      const { shiftDate, payroll, positionId, isJob } = subRow;

      if (isJob) return;

      const selectedPositionGroupId = positionId ? positionGroupByPositionId[positionId]?.id : null;
      if (!selectedPositionGroupId) return;

      if (!groupedByEmployeePosition[employeeId][selectedPositionGroupId]) {
        groupedByEmployeePosition[employeeId][selectedPositionGroupId] = [];
      }
      groupedByEmployeePosition[employeeId][selectedPositionGroupId].push(subRow);

      if (!payroll || !payroll.hours) {
        return;
      }

      const dayHours = (payroll.hours || []).find(({ slug }) => slug === 'DR')?.value || 0;
      const holidayHours = (payroll.hours || []).find(({ slug }) => slug === 'RR')?.value || 0;
      const nightHours = (payroll.hours || []).find(({ slug }) => slug === 'DOT1')?.value || 0;

      if (!hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId]) {
        hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId] = {};
      }

      if (!hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId][shiftDate]) {
        hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId][shiftDate] = { dayHours: 0, nightHours: 0 };
      }

      hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId][shiftDate].dayHours += dayHours + holidayHours;
      hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId][shiftDate].nightHours += nightHours;
    });
  });

  Object.entries(groupedByEmployeePosition).forEach(([employeeId, byGroupPositionData]) => {
    Object.entries(byGroupPositionData).forEach(([selectedPositionGroupId, positionDataRows]) => {
      const employeeNumberField = positionDataRows[0]?.employee?.identification;
      const shiftDate = positionDataRows[0]?.shiftDate;

      const correspondingPositionGroup = mappedPositionGroups[selectedPositionGroupId];
      //safty check
      if (!correspondingPositionGroup) return;

      const correspondingPositionGroupName = correspondingPositionGroup?.name?.split('-')?.[0]?.trim();

      const currentEmployeeHoursBucket = hoursBucketByEmployeeDate[employeeId][selectedPositionGroupId];

      const allZeros =
        !currentEmployeeHoursBucket || Object.values(currentEmployeeHoursBucket).every((item) => item.dayHours === 0);

      if (allZeros) return;

      const hoursParams = {
        //Keep empty
        Uitbetalen: '',
        //Keep empty
        Doorbetalen: ''
      };

      const employeeParams = {
        // employeeId
        PersoneelsNummer: employeeNumberField,
        //Keep empty
        TypeMedewerker: '',
        // Name
        Naam: [employeesMap[employeeId].firstName, employeesMap[employeeId].lastName]
          .filter((curr) => !!curr)
          .join(' '),
        //Payroll week
        Week: moment(shiftDate, API_DATE_FORMAT).week(),
        //Payroll year
        Jaar: moment(shiftDate, API_DATE_FORMAT).year(),
        ContractUren: '',
        Kostenplaats: 750401
      };

      data.push({
        ...employeeParams,
        //HNOR = normal hours, T135 = Night hours, Ziek = Sick
        //for each row, instead of 'HNOR' put the position code (if position id is null, put 'HNOR' )
        Code: selectedPositionGroupId && correspondingPositionGroup ? correspondingPositionGroupName : 'HNOR',
        ...hoursParams,
        //mondday
        ...Object.fromEntries(
          allDaysInRange.map(({ label, formattedDate }) => {
            const dayHoursValue = currentEmployeeHoursBucket?.[formattedDate]?.dayHours || 0;
            const formattedNumber = dayHoursValue.toString().replace('.', ',');

            return [showDateNextToLabel ? `${label} (${formattedDate})` : label, formattedNumber];
          })
        ),
        //Keep empty
        '402M': ''
      });

      // data.push({
      //   ...employeeParams,
      //   //HNOR = normal hours, T135 = Night hours, Ziek = Sick
      //   Code: 'T135',
      //   ...hoursParams,
      //   //mondday
      //   ...Object.fromEntries(
      //     allDaysInRange.map(({ label, formattedDate }) => {
      //       const nightHoursValue = currentEmplyeHoursBucket?.[formattedDate]?.nightHours || 0;
      //       const formattedNumber = nightHoursValue.toString().replace('.', ',');

      //       return [showDateNextToLabel ? `${label} (${formattedDate})` : label, formattedNumber];
      //     })
      //   ),

      //   //Keep empty
      //   '402M': ''
      // });
    });
  });

  exportExcel({
    baseData: data,
    showTitle: false,
    showColumnsRowStyle: false,
    name: 'Customized payroll',
    title: '',
    asCSV
  });
};

const getHoursByDay = ({ actualIn, payroll, type = 'hours' }) => {
  // Get the day of the week (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
  const dayOfStartTime = moment(actualIn).day();
  const payrollFieldId =
    type === 'travel'
      ? 2
      : type === 'weeklyPay'
      ? 458
      : type === 'lodgeAllowance'
      ? 652
      : type === 'lumpSum'
      ? 653
      : null;

  const days = [...new Array(7)].map((_, index) => {
    if (index === dayOfStartTime && payroll) {
      return type === 'hours'
        ? {
            regular: payroll.aggregatedHours.regular,
            overtime: payroll.aggregatedHours.overtime,
            overtime2: payroll.aggregatedHours.overtime2
          }
        : {
            value: payrollFieldId ? payroll.fields.find(({ id }) => id === payrollFieldId)?.value || 0 : 0
          };
    }

    return type === 'hours'
      ? {
          regular: 0,
          overtime: 0,
          overtime2: 0
        }
      : { value: 0 };
  });

  return days;
};

export const generatePayrollExportToMegaPay = ({
  accountId,
  timesheetData,
  mapPayroll,
  timeRange,
  search,
  filters
}) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const generalProjectAggregadtedData = {};

  groupedData.forEach(({ subRows = [] }) => {
    subRows.forEach(({ payroll, employee, projectId, invoiceNumber, wage }) => {
      const employeeId = employee.id;
      const hourlyRate = wage.hourlyWage;
      const employeeNumberField = employee.identification;

      if (!generalProjectAggregadtedData[employeeId]) {
        generalProjectAggregadtedData[employeeId] = {};
      }

      if (!generalProjectAggregadtedData[employeeId][projectId]) {
        generalProjectAggregadtedData[employeeId][projectId] = {};
      }

      if (!generalProjectAggregadtedData[employeeId][projectId][hourlyRate]) {
        generalProjectAggregadtedData[employeeId][projectId][hourlyRate] = MEGA_PAY_CODES?.[accountId]?.reduce(
          (all, { aggregationCode }) => {
            all[aggregationCode] = {
              value: 0
            };
            return all;
          },
          {}
        );
      }

      generalProjectAggregadtedData[employeeId][projectId][hourlyRate] = MEGA_PAY_CODES?.[accountId]?.reduce(
        (all, { type, code, aggregationCode, value, payRate }) => {
          const valueColumn = typeof value === 'function' ? value(payroll) : value;
          const payRateColumn = typeof payRate === 'function' ? payRate(payroll) : payRate;

          all[aggregationCode] = {
            ApplicantId: employeeNumberField,
            ProjectId: '',
            ProjectRefNo: invoiceNumber,
            PayrollNo: employeeNumberField,
            ElementCode: code,
            ElementType: type,
            value:
              (generalProjectAggregadtedData[employeeId][projectId][hourlyRate][aggregationCode].value || 0) +
              (valueColumn || 0),
            payRate: payRateColumn || 0,
            ElemeentProjectRefNo: invoiceNumber
          };

          return all;
        },
        {}
      );
    });
  });

  const data = Object.values(generalProjectAggregadtedData).reduce((all, projects) => {
    Object.values(projects).forEach((hourlyRate) => {
      Object.values(hourlyRate).forEach((hourlyRateRows = []) => {
        Object.values(hourlyRateRows).forEach((item) => {
          //skip rows with value of 0
          if (item.value !== 0) {
            all.push(item);
          }
        });
      });
    });

    return all;
  }, []);

  exportExcel({
    baseData: data,
    name: 'systemPayroll',
    title: '',
    detailedTable: false,
    showTitle: false,
    asCSV: true
  });
};

export const generateOnPayPayrollReport = ({ accountId, timesheetData, mapPayroll, timeRange, search, filters }) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const REGULAR_WORKER_FIELD = 40221;

  const generalProjectAggregadtedData = {};

  groupedData.forEach(({ subRows = [] }) => {
    const employee = subRows?.[0]?.employee;
    const isRegularWorker = employee?.fields.find(({ id }) => id === REGULAR_WORKER_FIELD)?.value?.[0] === '59809';

    const employeeNumberField = employee?.identification;
    const employeeId = employee?.id;

    subRows.forEach(({ payroll }) => {
      if (!payroll) {
        return;
      }
      const hourlyRate = payroll.wage.hourlyWage;

      if (!generalProjectAggregadtedData[employeeId]) {
        generalProjectAggregadtedData[employeeId] = {};
      }

      /* prepare for each hourly rate in employee the rows placeholders */
      if (!generalProjectAggregadtedData[employeeId][hourlyRate]) {
        generalProjectAggregadtedData[employeeId][hourlyRate] = ON_PAY_PAYROLL_CODES?.[accountId]?.reduce(
          (output, { payCode, price, qty, shiftExportColumns = false }) => {
            const payCodeColumn = payCode({ isRegularWorker });

            const priceColumn = typeof price === 'function' ? price({ payroll, isRegularWorker }) : price;
            const qtyColumn = typeof qty === 'function' ? qty({ payroll, isRegularWorker }) : qty;

            output[payCodeColumn] = {
              TYPE: 1,
              PAY_ID: payCodeColumn,
              EMP_NUM: employeeNumberField || '',
              HOURS_AMOUNT: qtyColumn,
              RATE: priceColumn,
              TREAT_AS_CASHE: 0,
              CASH_AMOUNT: 0,
              shiftExportColumns
            };
            return output;
          },
          {}
        );
      } else {
        ON_PAY_PAYROLL_CODES?.[accountId]?.forEach(
          ({ payCode, price, qty, isAggregatePrice = true, isAggregateQTY = true }) => {
            const payCodeColumn = payCode({ isRegularWorker });
            const priceColumn = typeof price === 'function' ? price({ payroll, isRegularWorker }) : price;
            const qtyColumn = typeof qty === 'function' ? qty({ payroll, isRegularWorker }) : qty;

            if (isAggregatePrice) {
              generalProjectAggregadtedData[employeeId][hourlyRate][payCodeColumn].RATE += priceColumn;
            }

            if (isAggregateQTY) {
              generalProjectAggregadtedData[employeeId][hourlyRate][payCodeColumn].HOURS_AMOUNT += qtyColumn;
            }
          },
          {}
        );
      }
    });
  });

  const data = Object.values(generalProjectAggregadtedData).reduce((all, employees) => {
    Object.values(employees).forEach((hourlyRates) => {
      Object.values(hourlyRates).forEach((payCodeData) => {
        if (!payCodeData.RATE || !payCodeData.HOURS_AMOUNT) {
          return;
        }

        const { TYPE, PAY_ID, EMP_NUM, HOURS_AMOUNT, RATE, shiftExportColumns } = payCodeData;

        all.push({
          TYPE,
          PAY_ID,
          EMP_NUM,
          HOURS_AMOUNT: shiftExportColumns ? 0 : HOURS_AMOUNT,
          RATE: shiftExportColumns ? 0 : RATE,

          //like hours amount
          TREAT_AS_CASHE: shiftExportColumns ? HOURS_AMOUNT : 0,
          //like rate
          CASH_AMOUNT: shiftExportColumns ? RATE : 0
        });
      });
    });

    return all;
  }, []);

  exportExcel({
    baseData: data,
    name: 'OnPay payroll report',
    title: '',
    detailedTable: false,
    showTitle: false
  });
};

export const generatePayrollExportToMegaPayPayslips = ({
  timesheetData,
  mapPayroll,
  timeRange,
  search,
  filters,
  mappedClientBranches,
  formattingMap
}) => {
  if (!timeRange) {
    return;
  }

  const { number } = formattingMap;

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  });

  const daysRow = `           MON TUE WED THU FRI SAT SUN UNITS RATE VALUE`;

  const getMultiplier = (value, fallback) => (value !== undefined && value > 0 ? value : fallback);

  const employeeAggregatedData = {};
  const generalProjectAggregadtedData = {};

  groupedData.forEach(({ subRows = [] }) => {
    subRows.forEach(({ payroll, projectId, employee, actualIn, multipliers }) => {
      const employeeId = employee?.id;

      const hoursByDay = getHoursByDay({ actualIn, payroll });
      const travelByDay = getHoursByDay({ actualIn, payroll, type: 'travel' });
      const weeklyPayByDay = getHoursByDay({ actualIn, payroll, type: 'weeklyPay' });
      const lodgeAllowanceByDay = getHoursByDay({ actualIn, payroll, type: 'lodgeAllowance' });
      const lumpSumeByDay = getHoursByDay({ actualIn, payroll, type: 'lumpSum' });

      if (!generalProjectAggregadtedData[employeeId]) {
        generalProjectAggregadtedData[employeeId] = {};
      }

      if (!generalProjectAggregadtedData[employeeId][projectId]) {
        generalProjectAggregadtedData[employeeId][projectId] = {
          hours: [...new Array(7)].map((_) => ({
            regular: 0,
            overtime: 0,
            overtime2: 0
          })),
          travel: [...new Array(7)].map((_) => ({ value: 0 })),
          weeklyPay: [...new Array(7)].map((_) => ({ value: 0 })),
          lodgeAllowance: [...new Array(7)].map((_) => ({ value: 0 })),
          lumpSum: [...new Array(7)].map((_) => ({ value: 0 })),
          totalValueRegular: 0,
          totalValueOvertime: 0,
          totalValueOvertime2: 0,
          displayMeta: {}
        };
      }

      const rate = payroll?.wage?.hourlyWage || 0;
      const hours = payroll?.aggregatedHours || {};
      const regularMultiplier = getMultiplier(multipliers?.regular, 1);
      const overtimeMultiplier = getMultiplier(multipliers?.overtime, 1.5);
      const overtime2Multiplier = getMultiplier(multipliers?.overtime2, 2);

      const prev = generalProjectAggregadtedData[employeeId][projectId];
      generalProjectAggregadtedData[employeeId][projectId] = {
        hours: prev.hours.map((item, itemIndex) => ({
          regular: item.regular + hoursByDay[itemIndex].regular,
          overtime: item.overtime + hoursByDay[itemIndex].overtime,
          overtime2: item.overtime2 + hoursByDay[itemIndex].overtime2
        })),
        travel: prev.travel.map((item, itemIndex) => ({
          value: item.value + travelByDay[itemIndex].value
        })),
        weeklyPay: prev.weeklyPay.map((item, itemIndex) => ({
          value: item.value + weeklyPayByDay[itemIndex].value
        })),
        lodgeAllowance: prev.lodgeAllowance.map((item, itemIndex) => ({
          value: item.value + lodgeAllowanceByDay[itemIndex].value
        })),
        lumpSum: prev.lumpSum.map((item, itemIndex) => ({
          value: item.value + lumpSumeByDay[itemIndex].value
        })),
        totalValueRegular: prev.totalValueRegular + (hours.regular || 0) * rate * regularMultiplier,
        totalValueOvertime: prev.totalValueOvertime + (hours.overtime || 0) * rate * overtimeMultiplier,
        totalValueOvertime2: prev.totalValueOvertime2 + (hours.overtime2 || 0) * rate * overtime2Multiplier,
        displayMeta: {
          regular: prev.displayMeta.regular || (hours.regular ? { rate, multiplier: regularMultiplier } : undefined),
          overtime:
            prev.displayMeta.overtime || (hours.overtime ? { rate, multiplier: overtimeMultiplier } : undefined),
          overtime2:
            prev.displayMeta.overtime2 || (hours.overtime2 ? { rate, multiplier: overtime2Multiplier } : undefined)
        }
      };
    });
  });

  groupedData.forEach(({ subRows = [], payrollTotal }) => {
    subRows.forEach(({ employee, name, clientBranchId, projectId, invoiceNumber, payroll, multipliers }) => {
      const employeeNumberField = employee?.identification;
      const employeeId = employee?.id;

      const projectData = generalProjectAggregadtedData[employeeId][projectId];
      const hoursByDay = projectData.hours;
      const travelByDay = projectData.travel;
      const weeklyPayByDay = projectData.weeklyPay;
      const lodgeAllowanceByDay = projectData.lodgeAllowance;
      const lumpSumByDay = projectData.lumpSum;

      const { wage = {}, value: payrollValue } = payroll || {};

      const totalRegular = hoursByDay.reduce((output, current) => output + current.regular, 0);
      const totalOvertime = hoursByDay.reduce((output, current) => output + current.overtime, 0);
      const totalOvertime2 = hoursByDay.reduce((output, current) => output + current.overtime2, 0);

      const totalValueRegular = number(projectData.totalValueRegular);
      const totalValueOvertime = number(projectData.totalValueOvertime);
      const totalValueOvertime2 = number(projectData.totalValueOvertime2);

      const hourlyRate = wage?.hourlyWage || 0;
      const regularMultiplier = getMultiplier(multipliers?.regular, 1);
      const overtimeMultiplier = getMultiplier(multipliers?.overtime, 1.5);
      const overtime2Multiplier = getMultiplier(multipliers?.overtime2, 2);

      // RATE reads per-row snapshots captured from timesheets that contributed those hours,
      // so display reflects the rate/multiplier actually used in the value calc.
      const regularDisplay = projectData.displayMeta?.regular;
      const overtimeDisplay = projectData.displayMeta?.overtime;
      const overtime2Display = projectData.displayMeta?.overtime2;

      const rateRegular = number(
        (regularDisplay?.multiplier ?? regularMultiplier) * (regularDisplay?.rate ?? hourlyRate)
      );
      const rateOvertime = number(
        (overtimeDisplay?.multiplier ?? overtimeMultiplier) * (overtimeDisplay?.rate ?? hourlyRate)
      );
      const rateOvertime2 = number(
        (overtime2Display?.multiplier ?? overtime2Multiplier) * (overtime2Display?.rate ?? hourlyRate)
      );

      /* text preparation */

      if (employeeAggregatedData[employeeId]?.projects[projectId]) {
        return;
      }

      const noWorkingHours = hoursByDay.every((item) => Object.values(item).every((value) => value === 0));
      if (noWorkingHours && !payrollValue) {
        return;
      }

      if (!employeeAggregatedData[employeeId]) {
        employeeAggregatedData[employeeId] = { projects: {} };
      }

      if (!employeeAggregatedData[employeeId]?.projects?.[projectId]) {
        employeeAggregatedData[employeeId].projects[projectId] = [];
      }

      const projects = employeeAggregatedData[employeeId].projects;

      /* {EventName} ~ {clientname} ~ {invoice number} ~ WE:{last day of caculated week}  */
      const projectDataRow = `${name ? `${name} ~ ` : ''}${
        clientBranchId ? mappedClientBranches[clientBranchId].name : ''
      } ~ ${invoiceNumber ? ` ~ ${invoiceNumber}` : ''} ~ WE:${moment(timeRange.end).format('DDMMYY').toUpperCase()} `;

      // regular
      const workedRegularHoursRow = `HRS WORK   ${hoursByDay
        .map((_, dayindex) => hoursByDay[(dayindex + 1) % hoursByDay.length].regular)
        .join('   ')}   ${totalRegular}   
        ${rateRegular}   
        ${totalValueRegular}`;

      // overtime
      const workedOvertimeHoursRow = `OT1.50    ${hoursByDay
        .map((_, dayindex) => hoursByDay[(dayindex + 1) % hoursByDay.length].overtime)
        .join('   ')}   
        ${totalOvertime}   
        ${rateOvertime}   
        ${totalValueOvertime}`;

      // overtime2
      const workedOvertime2HoursRow = `OT2.00    ${hoursByDay
        .map((_, dayindex) => hoursByDay[(dayindex + 1) % hoursByDay.length].overtime2)
        .join('   ')}   
        ${totalOvertime2}   
        ${rateOvertime2}   
        ${totalValueOvertime2}`;

      // travel
      const totalTravel = number(travelByDay.reduce((output, { value }) => output + value, 0));
      const travelRow = `TRAVEL    ${travelByDay
        .map((_, dayindex) => travelByDay[(dayindex + 1) % travelByDay.length].value)
        .join('   ')}   ${totalTravel}   ${number(regularMultiplier * hourlyRate)}   ${totalTravel}`;

      // weekly pay - 458
      const totalWeeklyPay = number(weeklyPayByDay.reduce((output, { value }) => output + value, 0));
      const weeklyPayRow = `WEEK/ADJ    ${weeklyPayByDay
        .map((_, dayindex) => weeklyPayByDay[(dayindex + 1) % weeklyPayByDay.length].value)
        .join('   ')}   ${totalWeeklyPay}   ${number(regularMultiplier * hourlyRate)}   ${totalWeeklyPay}`;

      // lodge allowance
      const totalLodgeAllowance = number(lodgeAllowanceByDay.reduce((output, { value }) => output + value, 0));
      const lodgeAllowanceRow = `LODGEALLOWANCE    ${lodgeAllowanceByDay
        .map((_, dayindex) => lodgeAllowanceByDay[(dayindex + 1) % lodgeAllowanceByDay.length].value)
        .join('   ')}   ${totalLodgeAllowance}   1   ${totalLodgeAllowance}`;

      // lump sum
      const totalLumpSum = number(lumpSumByDay.reduce((output, { value }) => output + value, 0));
      const lumpSumRow = `LUMPSUM    ${lumpSumByDay
        .map((_, dayindex) => lumpSumByDay[(dayindex + 1) % lumpSumByDay.length].value)
        .join('   ')}   ${totalLumpSum}   1   ${totalLumpSum}`;

      // row visibility
      const showOvertimeRow = hoursByDay.some((value) => value.overtime > 0);
      const showOvertimeRow2 = hoursByDay.some((value) => value.overtime2 > 0);
      const showTravelRow = travelByDay.some(({ value }) => value > 0);
      const showWeeklyPayRowRow = weeklyPayByDay.some(({ value }) => value > 0);
      const showLodgeAllowanceRowRow = lodgeAllowanceByDay.some(({ value }) => value > 0);
      const showLumpSumRowRow = lumpSumByDay.some(({ value }) => value > 0);

      // data
      projects[projectId].push({ ID: employeeNumberField, PayslipText: projectDataRow });
      projects[projectId].push({ ID: employeeNumberField, PayslipText: daysRow });
      projects[projectId].push({ ID: employeeNumberField, PayslipText: workedRegularHoursRow });

      showOvertimeRow && projects[projectId].push({ ID: employeeNumberField, PayslipText: workedOvertimeHoursRow });
      showOvertimeRow2 && projects[projectId].push({ ID: employeeNumberField, PayslipText: workedOvertime2HoursRow });
      showTravelRow && projects[projectId].push({ ID: employeeNumberField, PayslipText: travelRow });
      showWeeklyPayRowRow && projects[projectId].push({ ID: employeeNumberField, PayslipText: weeklyPayRow });
      showLodgeAllowanceRowRow && projects[projectId].push({ ID: employeeNumberField, PayslipText: lodgeAllowanceRow });
      showLumpSumRowRow && projects[projectId].push({ ID: employeeNumberField, PayslipText: lumpSumRow });

      employeeAggregatedData[employeeId].totalPayroll = {
        ID: employeeNumberField,
        PayslipText: `TOTAL EARNINGS: ${number(payrollTotal)}`
      };
    });
  });

  const data = Object.values(employeeAggregatedData).reduce((all, { projects, totalPayroll }) => {
    Object.values(projects).forEach((rows = []) => {
      all.push(...rows);
    });

    all.push(totalPayroll);

    return all;
  }, []);

  exportExcel({
    baseData: data,
    name: 'Payslips',
    title: '',
    asCSV: true
  });
};

export const MichpalCSV = ({
  t,
  groupedData,
  accountId,
  month,
  timeRange,
  fields: generalPayrollFields,
  employeesMap,
  companyData
}) => {
  let tableData = [];
  const { companyNumber } = companyData;

  const year = parseInt(timeRange.start.format('Y'), 10);
  const titleData = [{ 'מספר חברה': companyNumber, 'שנת המס': year, 'חודש עבודה': month }];

  if (accountId === 153) {
    groupedData.forEach(({ subRows }) => {
      const employeeIdField = subRows[0]?.employee.identification;

      subRows.forEach((row) => {
        const actualInHour = {
          תאריך: moment(row.shiftDate, API_DATE_TIME_FORMAT).format('DD/MM/YYYY'),
          שעה: row.actualIn ? moment(row.actualIn, API_DATE_TIME_FORMAT).format('HH:mm:ss') : '',
          'מספר עובד': employeeIdField || '',
          'קוד כניסה 1 יציאה 2': 1
        };

        const actualOutHour = {
          תאריך: moment(row.shiftDate, API_DATE_TIME_FORMAT).format('DD/MM/YYYY'),
          שעה: row.actualOut ? moment(row.actualOut, API_DATE_TIME_FORMAT).format('HH:mm:ss') : '',
          'מספר עובד': employeeIdField || '',
          'קוד כניסה 1 יציאה 2': 2
        };

        tableData.push(actualInHour, actualOutHour);
      });
    });
  }

  if (accountId === KAPAIM_ACCOUNT_ID) {
    const groupedDataByEmployeeAndRate = groupedData.reduce((output, currentEmployeeGroupedData) => {
      (currentEmployeeGroupedData.subRows || []).forEach((employeeSubRow) => {
        if (!employeeSubRow.wage) {
          return;
        }

        const { hourlyWage } = employeeSubRow.wage;

        if (!output[employeeSubRow.employee.id]) {
          output[employeeSubRow.employee.id] = {};
        }
        if (!output[employeeSubRow.employee.id][hourlyWage]) {
          output[employeeSubRow.employee.id][hourlyWage] = [];
        }

        output[employeeSubRow.employee.id][hourlyWage].push(employeeSubRow);
      });

      return output;
    }, {});

    Object.entries(groupedDataByEmployeeAndRate).forEach(([employeeIdString, employeeValue]) => {
      Object.entries(employeeValue).forEach(([hourlyRateString, data]) => {
        const { employee } = data[0];
        const { identification: employeeIdField, fields: employeeFields = [] } = employee;

        const hourlyRate = Number(hourlyRateString);
        const employeeId = Number(employeeIdString);

        const totalPayrollByRate = {
          regular: 0,
          overtime: 0,
          overtime2: 0,
          holidayRegular: 0,
          holidayOvertime: 0,
          holidayOvertime2: 0
        };

        const totalPayrollFields = { tip: 0, travel: 0, eshel: 0, bonus: 0 };
        let totalPayrollValue = 0;
        let actualTotalInHours = 0;

        data.forEach(({ payroll }) => {
          if (!payroll) {
            return;
          }

          const {
            aggregatedHours: { regular, overtime, overtime2, holidayRegular, holidayOvertime, holidayOvertime2 },
            aggregatedHours
          } = payroll;

          totalPayrollByRate.regular += regular;
          totalPayrollByRate.overtime += overtime;
          totalPayrollByRate.overtime2 += overtime2;
          totalPayrollByRate.holidayRegular += holidayRegular;
          totalPayrollByRate.holidayOvertime += holidayOvertime;
          totalPayrollByRate.holidayOvertime2 += holidayOvertime2;

          if (!payroll.fields) {
            return;
          }

          const { fields: payrollFields } = payroll;

          const payrollTipField = payrollFields.find(({ id }) => id === 1)?.value || 0;
          const payrollTravelField = payrollFields.find(({ id }) => id === 2)?.value || 0;
          const payrollAshelField = payrollFields.find(({ id }) => id === 362)?.value || 0;
          const payrollBonusField = payrollFields.find(({ id }) => id === 386)?.value || 0;

          totalPayrollFields.tip += payrollTipField;
          totalPayrollFields.travel += payrollTravelField;
          totalPayrollFields.eshel += payrollAshelField;
          totalPayrollFields.bonus += payrollBonusField;

          totalPayrollValue += payroll.value;
          actualTotalInHours += Object.values(aggregatedHours || {}).reduce((sum, value) => sum + value, 0);
        });

        tableData.push({
          'שם פרטי': employeesMap[employeeId].firstName,
          'שם משפחה': employeesMap[employeeId].lastName,
          'מספר עובד': employeeIdField || '',
          'סך שעות': actualTotalInHours,

          'סך שעות בסיס 100%': totalPayrollByRate.regular,
          'שכר שעתי- 100%': hourlyRate,

          'סך שעות בתעריף 125%': totalPayrollByRate.overtime,
          'תעריף שעות נוספות - 125%': hourlyRate * 1.25,

          'סך שעות בתעריף 150%': totalPayrollByRate.overtime2,
          'תעריף שעות נוספות - 150%': hourlyRate * 1.5,

          'סך שעות שבת 150%': totalPayrollByRate.holidayRegular,
          'תעריף שעות שבת - 150%': hourlyRate * 1.5,

          'סך שעות בתעריף 175%': totalPayrollByRate.holidayOvertime,
          'תעריף שעות נוספות - 175%': hourlyRate * 1.75,
          'סך שעות בתעריף 200%': totalPayrollByRate.holidayOvertime2,
          'תעריף שעות נוספות - 200%': hourlyRate * 2,

          'מספר משמרות': data?.length,
          טיפ: totalPayrollFields.tip,
          נסיעות: totalPayrollFields.travel,
          'סה״כ': totalPayrollValue,
          'אש״ל': totalPayrollFields.eshel,
          בונוס: totalPayrollFields.bonus,
          'תעודת זהות': employeeFields?.find(({ id }) => id === 22537)?.value || ''
        });
      });
    });
  } else {
    groupedData.forEach((data) => {
      const { groupValue, actualTotal, aggregatedHoursTotalRow, subRows, fieldsTotal } = data;
      const {
        regular,
        overtime,
        overtime2,
        holidayRegular,
        holidayOvertime,
        holidayOvertime2
      } = aggregatedHoursTotalRow;

      const { payroll, employee, wage } = subRows?.[0] || {};
      const { fields } = payroll || {};
      const hourlyRate = wage?.hourlyWage || 0;

      const employeeIdField = employee?.identification;

      const actualHours = actualTotal / 1000 / 60 / 60;

      const payrollFieldsById = fields?.reduce((all, current) => ({ ...all, [current.id]: current.value }), {});
      const payrollFieldsValues = (generalPayrollFields || []).reduce(
        (output, current) => ({
          ...output,
          [current.id]: payrollFieldsById?.[current.id] || current.value
        }),
        {}
      );

      tableData.push({
        'מספר עובד': employeeIdField || '',
        'שם פרטי': employeesMap[groupValue].firstName,
        'שם משפחה': employeesMap[groupValue].lastName,
        'שעות רגילות': regular,
        'שכר שעתי': hourlyRate,
        'סך שעות בתעריף 125%': overtime,
        'תעריף שעות נוספות - 125%': hourlyRate * 1.25,
        'סך שעות בתעריף 150%': overtime2 + holidayRegular,
        'תעריף שעות נוספות - 150%': hourlyRate * 1.5,
        'סך שעות בתעריף 175%': holidayOvertime,
        'תעריף שעות נוספות - 175%': hourlyRate * 1.75,
        'סך שעות בתעריף 200%': holidayOvertime2,
        'תעריף שעות נוספות - 200%': hourlyRate * 2,
        'מספר משמרות': subRows?.length,
        //   'ערך קבוע הוצאות נסיעה קבועות': globalTravelingField,
        //   'הוצאות נסיעה קבועות': globalTravelingField * actualHours,
        //   'ערך תוספת גלובלית': globalAdditionHourlyField,
        //   'תוספת גלובלית': globalAdditionHourlyField * actualHours,
        //   'הוצאות נסיעה': payrollTravelField * numberOfShifts,
        //   'השלמת שכר': payrollTipField * numberOfShifts,
        //   'טיפ כללי': motsiField,
        'סך שעות': actualHours,
        ...(generalPayrollFields || []).reduce((output, current, index) => {
          const slug = current.slug || `other ${index}`;
          const columnNameValue = `ערך ${slug}`;
          const columnNameTotal = `סך ${slug}`;

          return {
            ...output,
            [columnNameValue]: payrollFieldsValues?.[current.id] || 0,
            [columnNameTotal]: fieldsTotal?.[current.id] || 0
          };
        }, {})
      });
    });
  }

  smartExportExcel({
    baseData: tableData || [],
    name: t('מכפל'),
    title: '',
    detailedTable: titleData,
    showTitle: false
  });
};

const generalPayrollGroupByWage = ({
  groupedData,
  columns,
  t,
  from,
  to,
  mappedFieldsValue,
  employeesMap,
  accountId,
  accountName,
  accountFields
}) => {
  const customPayrollFields = (columns || []).filter(({ datakey }) => datakey?.isPayrollField);
  const customPayrollFieldsIds = new Set(customPayrollFields.map(({ datakey }) => datakey.id));

  const customPayrollFieldsSlugs = customPayrollFields.map(({ datakey }) => datakey.slug);

  const getEmployeeFields = (id) => {
    if (!id) {
      return [];
    }

    const employeeFieldsMapping = getValuesOfFields(employeesMap[id]?.fields, accountFields, mappedFieldsValue);

    return (accountFields || []).reduce((output, { id: fieldId, name }) => {
      output[name] = employeeFieldsMapping[fieldId]?.value || '';
      return output;
    }, {});
  };

  const rowData = groupedData
    .reduce((prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))], [])
    .map((row) => {
      const employeeFields = getEmployeeFields(row?.employee?.id);

      return {
        ...(row?.payroll?.fields || [])
          .filter(({ id }) => customPayrollFieldsIds.has(id))
          .reduce((acc, { slug, value }) => {
            acc[slug] = value;
            return acc;
          }, {}),

        ...(row?.payroll?.fields || [])
          .filter(({ id }) => customPayrollFieldsIds.has(id))
          .reduce((output, current, index) => {
            const slug = `${current.slug} ${t('total')}` || `other ${index}`;
            return {
              ...output,
              [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.total || ''
            };
          }, {}),

        id: row?.employee?.id,
        employeeId: row.employee?.fields?.find(({ id }) => id === 7335)?.value || row.employee?.identification,
        tip:
          (row?.payroll?.fields || [])?.find(({ id }) => {
            if (accountId === 7036) {
              return id === PAYROLL_FIELD_TIP_ID_7036;
            }
            return id === PAYROLL_FIELD_TIP_ID;
          })?.value || 0,
        travel: (row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_ID)?.value || 0,
        firstName: row.employee.firstName,
        lastName: row.employee.lastName,
        ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
          (prev, key) => ({ ...prev, [key]: row.payroll?.aggregatedHours?.[key] || 0 }),
          {}
        ),
        totalHours:
          (row.payroll?.aggregatedHours?.regular || 0) +
          (row.payroll?.aggregatedHours?.overtime || 0) +
          (row.payroll?.aggregatedHours?.overtime2 || 0) +
          (row.payroll?.aggregatedHours?.holidayRegular || 0) +
          (row.payroll?.aggregatedHours?.holidayOvertime || 0) +
          (row.payroll?.aggregatedHours?.holidayOvertime2 || 0),
        hourlyRate: row.wage?.hourlyWage || 0,
        flatWage: row.wage?.flatWage || 0,
        flatHours: row.wage?.flatHours || 0,
        totalPayroll: row.payroll?.value || 0,
        ...employeeFields
      };
    });

  const groupByWageData = Object.values(
    rowData.reduce(
      (
        acc,
        {
          id,
          employeeId,
          firstName,
          lastName,
          totalHours,
          regular,
          overtime,
          overtime2,
          holidayOvertime,
          holidayOvertime2,
          holidayRegular,
          travel,
          tip,
          hourlyRate,
          flatWage,
          totalPayroll,
          ...rest
        }
      ) => {
        const combination = `${id}-${flatWage}-${hourlyRate}`;
        const employeeID = isNaN(employeeId) ? employeeId : Number(employeeId);
        const employeeFields = getEmployeeFields(id);

        const payload = acc[combination] || {
          firstName,
          lastName,
          'employee-id': employeeID || '',
          hourlyRate: 0,
          flatWage: 0,
          totalHours: 0,
          regular: 0,
          overtime: 0,
          overtime2: 0,
          holidayOvertime: 0,
          holidayOvertime2: 0,
          holidayRegular: 0,
          numOfShifts: 0,
          tip: 0,
          travel: 0,
          totalPayroll: 0,
          ...customPayrollFieldsSlugs.reduce((prev, slug) => {
            prev[slug] = 0;
            return prev;
          }, {}),

          ...customPayrollFieldsSlugs.reduce((prev, current, index) => {
            const slug = `${current.slug} ${t('total')}` || `other ${index}`;
            prev[slug] = 0;
            return prev;
          }, {}),

          ...Object.keys(employeeFields).reduce((prev, key) => {
            prev[key] = '';
            return prev;
          }, {})
        };

        customPayrollFieldsSlugs.forEach((slug) => {
          payload[slug] = payload[slug] + (rest[slug] || 0);
        });

        payload.totalHours = payload.totalHours + totalHours;
        payload.regular = payload.regular + regular;
        payload.overtime = payload.overtime + overtime;
        payload.overtime2 = payload.overtime2 + overtime2;
        payload.holidayRegular = payload.holidayRegular + holidayRegular;
        payload.holidayOvertime = payload.holidayOvertime + holidayOvertime;
        payload.holidayOvertime2 = payload.holidayOvertime2 + holidayOvertime2;
        payload.hourlyRate = hourlyRate;
        payload.flatWage = flatWage;
        payload.tip = payload.tip + tip;
        payload.travel = payload.travel + travel;
        payload.totalPayroll = payload.totalPayroll + totalPayroll;
        payload.numOfShifts += 1;

        Object.entries(employeeFields).forEach(([key, value]) => {
          payload[key] = value || '';
        });

        acc[combination] = payload;

        return acc;
      },
      {}
    )
  );

  const results = groupByWageData.map((data) => conditionalColumns(data, columns));

  exportExcel({
    baseData: results,
    name: [accountName, t('employeeRateReport'), from, to].join(' - '),
    showTitle: false
  });
};

const generalPayrollGroupByPosition = ({ groupedData, columns, t, from, to, mappedPositions, accountName }) => {
  const rowData = groupedData
    .reduce((prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))], [])
    .map((row) => ({
      employeeId: row?.employee?.id,
      employee: [row?.employee?.firstName, row?.employee?.lastName].filter((cur) => !!cur).join(', '),
      position: mappedPositions[row.positionId]?.label,
      positionId: row.positionId,
      regularHours: row.payroll?.aggregatedHours?.regular || 0,
      overtimeHours: (row.payroll?.aggregatedHours?.overtime || 0) + (row.payroll?.aggregatedHours?.overtime2 || 0),
      hourlyRate: row.wage?.hourlyWage || 0
    }));

  const groupByPosition = rowData.reduce(
    (acc, { employeeId, employee, position, positionId, regularHours, overtimeHours, hourlyRate }) => {
      const comb = [employeeId, positionId].join(',');
      const payload = acc[comb] || {
        employee,
        position,
        regularHours,
        overtimeHours,
        hourlyRate,
        regularPay: 0,
        overtimePay: 0,
        totalPayroll: 0
      };

      payload.regularHours += regularHours;
      payload.overtimeHours += overtimeHours;

      const regularPay = regularHours * hourlyRate;
      payload.regularPay += regularPay;

      const overtimePay = regularHours * overtimeHours;
      payload.overtimePay += overtimePay;

      payload.totalPayroll += regularPay + overtimePay;

      acc[comb] = payload;

      return acc;
    },
    {}
  );

  const results = Object.values(groupByPosition).map((data) => conditionalColumns(data, columns));

  exportExcel({
    baseData: results,
    name: [accountName, t('employeeBasePositionReport'), from, to].join(' - '),
    showTitle: false
  });
};

export const generalPayrollResourcingEdgeReport = ({
  dateFormat,
  mappedLocations,
  mappedPositions,
  mapPayroll,
  timesheetData
}) => {
  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false
  });

  if (groupedData?.length <= 0) {
    return;
  }

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  if (groupedData.length === 0) {
    return [];
  }

  const results = [];

  groupedData.forEach((data) => {
    const { groupValue, subRows } = data;

    const firstName = employeesMap[groupValue]?.firstName;
    const lastName = employeesMap[groupValue]?.lastName;
    const ssnField = subRows?.[0].employee?.fields?.find(({ id }) => id === 9035)?.value;

    (subRows || [])
      .filter((row) => row.actualOut && row.actualIn)
      .forEach((row) => {
        const sharedItemsPart1 = {
          SSN: ssnField,
          'Last name': lastName,
          'First name': firstName
        };

        const sharedItemsPart2 = {
          DateWorked: moment(row.isJob ? row.date : row.shiftDate, API_DATE_TIME_FORMAT).format(dateFormat),
          PayRate: row?.wage?.hourlyWage || 0,
          WCCode: '9079',
          POSITION: mappedPositions[row.positionId]?.label || '',
          LOCATIONCODE: row.isJob
            ? 'TASA CORP OFC - 1'
            : row.locationId
            ? mappedLocations[row.locationId]?.name?.split('-')?.[1]?.trim() || ''
            : '',
          DEPARTMENT: 'Temp'
        };

        const sharedItemsPart3 = { 'One Time Deduction Code': '', 'One Time Deduction Amount': '' };

        results.push({
          ...sharedItemsPart1,
          PayCode: 'REG',
          HoursWorked: row.payroll.aggregatedHours.regular,
          ...sharedItemsPart2,
          TIPSCHARGED: (row.payroll.fields || []).find(({ id }) => id === PAYROLL_FIELD_TIP_ID)?.value || 0,
          ...sharedItemsPart3
        });

        const totalOvertimeHours =
          row.payroll.aggregatedHours.overtime +
          row.payroll.aggregatedHours.overtime2 +
          row.payroll.aggregatedHours.holidayRegular +
          row.payroll.aggregatedHours.holidayOvertime +
          row.payroll.aggregatedHours.holidayOvertime2;

        if (totalOvertimeHours > 0) {
          results.push({
            ...sharedItemsPart1,
            PayCode: 'OT',
            HoursWorked: totalOvertimeHours,
            ...sharedItemsPart2,
            TIPSCHARGED: '',
            ...sharedItemsPart3
          });
        }
      });
  });

  if (results.length === 0) {
    return [];
  }

  exportExcel({
    baseData: results || [],
    name: 'TASAPayrollReport',
    title: '',
    detailedTable: null,
    showTitle: false
  });
};

export const generalPayrollHeartlandReport = ({ mappedFieldsValue, mapPayroll, timesheetData }) => {
  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false
  });

  if (groupedData?.length <= 0) {
    return;
  }

  const employeesMap = getEmployeesMap(timesheetData?.employees);

  if (groupedData.length === 0) {
    return [];
  }
  const results = flatten(
    groupedData.map((data) => {
      const { groupValue, subRows } = data;

      const employeeID = employeesMap[groupValue].identification;

      const departmentFieldValue = subRows?.[0].employee?.fields?.find(({ id }) => id === 137)?.value;
      const departmentValue = departmentFieldValue ? mappedFieldsValue?.[137](departmentFieldValue) : '';

      const employeeNumber = employeeID || '';
      const fullName = `${employeesMap[groupValue].firstName} ${employeesMap[groupValue].lastName}`;
      const hourlyRate = subRows?.[0].wage?.hourlyWage || 0;
      const departmentCode = departmentValue || '';
      const shiftHoursRegular = subRows?.[0].payroll?.aggregatedHours?.regular || 0;
      const shiftHoursOvertime =
        subRows?.[0].payroll?.aggregatedHours?.overtime || 0 + subRows?.[0].payroll?.aggregatedHours?.overtime2 || 0;
      const tip = (subRows?.[0]?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TIP_ID)?.value || 0;
      const travel =
        (subRows?.[0]?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_ID)?.value || 0;

      const companyIdPadded = charPad('AosTest', 10, ' ', false);
      const employeeNumberPadded = charPad(employeeNumber, 9, ' ');
      const employeeDevisionPadded = charPad('', 10, ' ');
      const departmentCodePadded = charPad(departmentCode, 12, ' ');
      const clockNumberPadded = charPad('', 9, ' ');
      const fullNamePadded = charPad(fullName, 30, ' ', false);

      const regularHoursPadded = charPad(shiftHoursRegular.toFixed(2), 10, '0');
      const extraHours = charPad(shiftHoursOvertime.toFixed(2), 10, '0');
      const tipPadded = charPad(tip.toFixed(2), 10, '0');
      const travelPadded = charPad(travel.toFixed(2), 10, '0');
      const ratePadded = charPad(hourlyRate.toFixed(4), 8, '0');
      const rateExtraPadded = charPad((hourlyRate * 1.25).toFixed(4), 8, '0');

      const format = (line) =>
        `${companyIdPadded}${employeeNumberPadded}${employeeDevisionPadded}${line}${departmentCodePadded}${clockNumberPadded}${fullNamePadded}`;
      const lines = [];

      let earningCode = charPad('01', 15, ' ');
      lines.push(format(`${earningCode}H${regularHoursPadded}+${ratePadded}`));

      if (shiftHoursOvertime > 0) {
        earningCode = charPad('01', 15, ' ');
        lines.push(format(`${earningCode}H${extraHours}+${rateExtraPadded}`));
      }

      if (tip > 0) {
        earningCode = charPad('66', 15, ' ');
        // eslint-disable-next-line no-useless-escape
        lines.push(format(`${earningCode}\$${tipPadded}+00000000`));
      }

      if (travel > 0) {
        earningCode = charPad('44', 15, ' ');
        // eslint-disable-next-line no-useless-escape
        lines.push(format(`${earningCode}\$${travelPadded}`));
      }

      return lines;
    })
  );

  exportTxt((results || []).join('\n'), 'heartland_report.txt');
};

export const generalPayrollHeartlandReportCSV = (props) => {
  const columns = Object.entries({
    legalField: 'Legal',
    payGroupField: 'PayGroup',
    divisionField: 'Division',
    departmentField: 'Department',
    employeeId: 'key',
    name: 'Name',
    laborValue1: 'LaborValue1',
    laborValue2: 'LaborValue2',
    laborValue3: 'LaborValue3',
    eRegularHours: 'E_Regular_Hours',
    eOvertimeHours: 'E_Overtime_Hours',
    eDoubletimeHours: 'E_Doubletime_Hours',
    ePaycheckTipsDollars: 'E_Paycheck Tips_Dollars',
    eSickLeaveHours: 'E_Sick Leave_Hours',
    eMileageReimbHours: 'E_Mileage Reimb_Hours',
    eMileageReimbDollars: 'E_Mileage Reimb_Dollars',
    eContractorHours: 'E_Contractor_Hours',
    eContractorTipsDollars: 'E_Contractor Tips_Dollars',
    eExpenseReimbDollars: 'E_Expense Reimb_Dollars'
  }).map(([column, label]) => ({ Header: label, dataKey: column }));
  return generateCSV({
    ...props,
    columns,
    summarized: false,
    rowTransformer: (row, extraRowsData) => {
      const { fields: payrollFields = [] } = extraRowsData?.payroll || {};
      const { fields: employeeFields = [] } = extraRowsData?.employee || [];

      const mappedPayrollFieldsValues = mappedArray(payrollFields);
      const mappedEmployeeFieldsValues = mappedArray(employeeFields);

      const data = {
        legalField: mappedEmployeeFieldsValues[18460]?.value,
        payGroupField: mappedEmployeeFieldsValues[18461]?.value,
        divisionField: mappedEmployeeFieldsValues[18462]?.value,
        departmentField: mappedEmployeeFieldsValues[18463]?.value,

        employeeId: (extraRowsData?.employee?.identification || '').toString(),
        name: [extraRowsData?.employee?.lastName || '', extraRowsData?.employee?.firstName || ''].join(', '),

        laborValue1: mappedEmployeeFieldsValues[18462]?.value,
        laborValue2: mappedEmployeeFieldsValues[18463]?.value,
        laborValue3: props?.mappedPositions?.[extraRowsData?.positionId]?.label?.split('-')[0],

        eRegularHours: row.regular,
        eOvertimeHours: row.overtime,
        eDoubletimeHours: row.overtime2,

        ePaycheckTipsDollars: mappedPayrollFieldsValues[1]?.value,
        eSickLeaveHours: mappedPayrollFieldsValues[378]?.value,
        eMileageReimbHours: mappedPayrollFieldsValues[376]?.value,
        eMileageReimbDollars: mappedPayrollFieldsValues[377]?.value,
        eContractorHours: mappedPayrollFieldsValues[379]?.value,
        eContractorTipsDollars: mappedPayrollFieldsValues[380]?.value,
        eExpenseReimbDollars: mappedPayrollFieldsValues[381]?.value
      };

      return Object.fromEntries(columns.map(({ dataKey }) => [dataKey, data[dataKey] || '']));
    }
  });
};

const generalPayrollSalarySystem = ({ groupedData, columns, t, from, to, accountName }) => {
  if (groupedData.length === 0) {
    return [];
  }

  const workingMonth = moment(groupedData?.[0]?.subRows?.[0]?.date).get('M') + 1;

  const rowData = groupedData
    .reduce((prev, { subRows }) => [...prev, ...subRows.map((row) => row)], [])
    .filter((row) => row.actualOut && row.actualIn)
    .sort((rowA, rowB) => {
      const aValue = rowA.employee?.fields?.find(({ id }) => id === 4848)?.value;
      const bValue = rowB.employee?.fields?.find(({ id }) => id === 4848)?.value;

      return aValue?.localeCompare?.(bValue) || 0;
    })

    .map((row) => {
      const isIndependenceDay1 = row?.date === '2022-05-04 00:00:00';
      const isIndependenceDay2 = row?.date === '2022-05-05 00:00:00';
      const result = {
        id: row.employee?.identification,
        employeeIdField: row.employee?.fields?.find(({ id }) => id === 4848)?.value,
        shiftHours:
          row.payroll.aggregatedHours.regular +
          row.payroll.aggregatedHours.overtime +
          row.payroll.aggregatedHours.overtime2 +
          row.payroll.aggregatedHours.holidayRegular +
          row.payroll.aggregatedHours.holidayOvertime +
          row.payroll.aggregatedHours.holidayOvertime2,
        shiftHoursRegular: row.payroll.aggregatedHours.regular,
        payrollTotal: row.payroll.value,
        tip:
          ((row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TIP_ID)?.value || 0) +
          ((row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TIP_BONUS_ID)?.value || 0),

        travel:
          ((row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_ID)?.value || 0) +
          ((row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TRAVEL_PARKING_ID)?.value || 0),

        overtime: row.payroll.aggregatedHours.overtime,
        overtime2: row.payroll.aggregatedHours.overtime2,
        ...(isIndependenceDay1
          ? {
              holidayRegular:
                row.payroll.aggregatedHours.holidayRegular +
                row.payroll.aggregatedHours.holidayOvertime +
                row.payroll.aggregatedHours.holidayOvertime2,
              holidayOvertime: 0,
              holidayOvertime2: 0
            }
          : isIndependenceDay2
          ? {
              holidayRegular: 0,
              holidayOvertime: 0,
              holidayOvertime2:
                row.payroll.aggregatedHours.holidayRegular +
                row.payroll.aggregatedHours.holidayOvertime +
                row.payroll.aggregatedHours.holidayOvertime2
            }
          : {
              holidayRegular: row.payroll.aggregatedHours.holidayRegular,
              holidayOvertime: row.payroll.aggregatedHours.holidayOvertime,
              holidayOvertime2: row.payroll.aggregatedHours.holidayOvertime2
            })
      };
      return result;
    });

  const groupByMonthData = Object.values(
    rowData.reduce(
      (
        acc,
        {
          id,
          shiftHours,
          shiftHoursRegular,
          tip,
          travel,
          overtime,
          overtime2,
          holidayRegular,
          holidayOvertime,
          holidayOvertime2,
          payrollTotal,
          ...rest
        }
      ) => {
        const payload = acc[id] || {
          ...rest,
          id,
          numOfShifts: 0,
          shiftHours: 0,
          shiftHoursRegular: 0,
          travel: 0,
          tip: 0,
          payrollTotal: 0,
          holidayRegular: 0,
          holidayOvertime: 0,
          holidayOvertime2: 0,
          overtime: 0,
          overtime2: 0
        };

        payload.numOfShifts += 1;
        payload.shiftHours += shiftHours;
        payload.shiftHoursRegular += shiftHoursRegular;
        payload.travel += travel;
        payload.tip += tip;
        payload.overtime += overtime;
        payload.overtime2 += overtime2;
        payload.holidayRegular += holidayRegular;
        payload.holidayOvertime += holidayOvertime;
        payload.payrollTotal += payrollTotal;
        payload.holidayOvertime2 += holidayOvertime2;

        acc[id] = payload;

        return acc;
      },
      {}
    )
  );

  const payloads = [];

  const MINIMUM_HOURLY_RATE = 32.31;

  groupByMonthData.forEach(
    ({
      id,
      employeeIdField,
      shiftHours,
      shiftHoursRegular,
      tip,
      travel,
      overtime,
      payrollTotal,
      overtime2,
      holidayRegular,
      holidayOvertime,
      holidayOvertime2,
      numOfShifts
    }) => {
      [
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 4,
          code: 5,
          price: 1,
          quantity: shiftHours
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 4,
          code: 7,
          price: 1,
          quantity: numOfShifts
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 4,
          code: 4,
          price: 1,
          quantity: numOfShifts
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 1,
          price: MINIMUM_HOURLY_RATE,
          quantity: shiftHoursRegular
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 3,
          price: travel.toFixed(2),
          quantity: travel === 0 ? 0 : 1
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 4,
          price: 1.5,
          quantity: shiftHours
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 834,
          price: (MINIMUM_HOURLY_RATE * 1.25).toFixed(2),
          quantity: overtime
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 835,
          price: (MINIMUM_HOURLY_RATE * 1.5).toFixed(2),
          quantity: overtime2
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 182,
          price: tip.toFixed(2),
          quantity: tip === 0 ? 0 : 1
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 167,
          price: holidayOvertime2 > 0 ? (MINIMUM_HOURLY_RATE * 2).toFixed(2) : 0,
          quantity: holidayOvertime2
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 171,
          price: (MINIMUM_HOURLY_RATE * 1.75).toFixed(2),
          quantity: holidayOvertime
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 836,
          price: (MINIMUM_HOURLY_RATE * 1.5).toFixed(2),
          quantity: holidayRegular
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 832,
          price: (
            payrollTotal +
            -1 *
              (MINIMUM_HOURLY_RATE * shiftHoursRegular +
                MINIMUM_HOURLY_RATE * 1.25 * overtime +
                MINIMUM_HOURLY_RATE * 1.5 * overtime2 +
                MINIMUM_HOURLY_RATE * 1.5 * holidayRegular +
                MINIMUM_HOURLY_RATE * 1.75 * holidayOvertime +
                MINIMUM_HOURLY_RATE * 2 * holidayOvertime2 +
                travel +
                tip +
                1.5 * shiftHours)
          ).toFixed(2),
          quantity:
            payrollTotal +
              -1 *
                (MINIMUM_HOURLY_RATE * shiftHoursRegular +
                  MINIMUM_HOURLY_RATE * 1.25 * overtime +
                  MINIMUM_HOURLY_RATE * 1.5 * overtime2 +
                  MINIMUM_HOURLY_RATE * 1.5 * holidayRegular +
                  MINIMUM_HOURLY_RATE * 1.75 * holidayOvertime +
                  MINIMUM_HOURLY_RATE * 2 * holidayOvertime2 +
                  travel +
                  tip +
                  1.5 * shiftHours) ===
            0
              ? 0
              : 1
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 1500,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 831,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 833,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 900,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 901,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 902,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 903,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 904,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 905,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 906,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 907,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 908,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 909,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 910,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 911,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 913,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 164,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 165,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 914,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 2001,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 2004,
          price: 0,
          quantity: 0
        },
        {
          workingMonth,
          employeeIdSalaryReport: id,
          employeeIdField,
          columnKind: 1,
          code: 119,
          price: 0,
          quantity: 0
        }
      ].forEach((payload) => {
        payloads.push(payload);
      });
    }
  );

  const results = payloads.map((data) => conditionalColumns(data, columns));

  exportExcel({
    baseData: results,
    name: [accountName, t('employeeRateReport'), from, to].join(' - '),
    showTitle: false,
    autoFilter: false
  });
};

const generalPayrollDetailedWithSummariesHasira = ({
  groupedData,
  employeesMap,
  t,
  dateFormat,
  timeFormat,
  mappedTags,
  firstDayOfWeek,
  dateSorter,
  mappedPositions,
  accountName
}) => {
  const groupedDataSorted = groupedData.sort((a, b) => SORT_BY_NAME(a.subRows?.[0], b.subRows?.[0]));
  groupedDataSorted.forEach(({ subRows }) => subRows.sort(dateSorter));

  groupedDataSorted.forEach((data) => {
    const { groupValue, actualTotal, aggregatedHoursTotalRow, subRows, payrollTotal, clientWageTotal } = data;

    const { totalBreaks } = subRows.reduce(
      ({ totalBreaks: oldTotalBreaks }, row) => {
        const { breaktimes = [] } = row;

        return {
          totalBreaks:
            oldTotalBreaks + breaktimes.reduce((sum, { startTime, endTime }) => sum + totalTime(startTime, endTime), 0)
        };
      },
      { totalBreaks: 0 }
    );

    const employee = employeesMap[groupValue];
    const employeeIdField = isNaN(employee.identification) ? employee.identification : Number(employee.identification);

    const { regular, overtime, overtime2, holidayRegular, holidayOvertime, holidayOvertime2 } = aggregatedHoursTotalRow;
    data.subRows.push({
      summary: true,
      id: employeeIdField || '',
      firstName: employee.firstName,
      lastName: employee.lastName,
      actualTotal,
      regular,
      holidayRegular,
      holidayOvertime,
      overtime,
      holidayOvertime2,
      overtime2,
      totalShifts: subRows.length,
      totalBreaks,
      payrollTotal,
      clientWageTotal
    });
  });

  const groupedDataSummarized = groupedDataSorted.reduce(
    (prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))],
    []
  );

  const columns = [
    'employee-id',
    'date',
    'day',
    'type',
    'firstName',
    'lastName',
    'position',
    'tags',
    'in',
    'out',
    'totalHours',
    'breaks',
    'paidHours',
    '100%',
    '125%',
    '150%',
    '175%',
    '200%',
    'hourlyRate',
    'payrollNotes',
    'total'
  ].map((column) => ({ Header: t(columnHeaderKey(column)), dataKey: column }));

  const visibleData = groupedDataSummarized.map((row) => {
    if (row.summary) {
      const {
        id: employeeIdField,
        firstName,
        lastName,
        totalShifts,
        regular,
        holidayRegular,
        holidayOvertime,
        overtime,
        totalBreaks,
        actualTotal,
        holidayOvertime2,
        overtime2,
        payrollTotal
      } = row;

      const paidHours = actualTotal - (totalBreaks || 0);
      const overtimeSummary = {
        100: regular,
        125: overtime,
        150: overtime2 + holidayRegular,
        175: holidayOvertime,
        200: holidayOvertime2
      };

      return conditionalColumns(
        {
          'employee-id': employeeIdField,
          date: totalShifts,
          day: '',
          type: `${t('monthlyCalculation')} : ${t('decimal')}`,
          firstName,
          lastName,
          position: '',
          tags: '',
          in: '',
          out: '',
          checkIn: '',
          checkOut: '',
          totalHours: actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(2)) : '',
          breaks: totalBreaks > 0 ? Number(moment.duration(totalBreaks).asHours().toFixed(2)) : '',
          paidHours: paidHours ? Number(moment.duration(paidHours).asHours().toFixed(2)) : '',
          '100%': overtimeSummary[100] ? Number(overtimeSummary[100]).toFixed(2) : '',
          '125%': overtimeSummary[125] ? Number(overtimeSummary[125]).toFixed(2) : '',
          '150%': overtimeSummary[150] ? Number(overtimeSummary[150]).toFixed(2) : '',
          '175%': overtimeSummary[175] ? Number(overtimeSummary[175]).toFixed(2) : '',
          '200%': overtimeSummary[200] ? Number(overtimeSummary[200]).toFixed(2) : '',
          hourlyRate: '',
          payrollNotes: '',
          total: payrollTotal || ''
        },
        columns
      );
    }

    const totalBreaks = (row.breaktimes || []).reduce(
      (sum, { startTime, endTime }) => sum + totalTime(startTime, endTime),
      0
    );

    const isWeekend = () => {
      const dayOfWeek = moment(row.shiftDate, API_DATE_TIME_FORMAT).day();
      if (firstDayOfWeek === 1) {
        return dayOfWeek === 5 || dayOfWeek === 6; // sunday - 0
      }
      if (firstDayOfWeek === 2) {
        return dayOfWeek === 0 || dayOfWeek === 6;
      }
      return false;
    };

    const hours =
      row.actualOut && row.actualIn
        ? moment.duration(moment(row.actualOut).diff(moment(row.actualIn)))
        : moment.duration(0);

    const { aggregatedHours } = row.payroll || {};

    const employeeID = isNaN(row.employee.identification)
      ? row.employee.identification
      : Number(row.employee.identification);

    return conditionalColumns(
      {
        'employee-id': employeeID || '',
        date: moment(row.shiftDate, API_DATE_TIME_FORMAT).format(dateFormat),
        day: moment(row.shiftDate, API_DATE_TIME_FORMAT).format('dddd'),
        type: isWeekend() ? t('weekend') : t('weekday'),
        firstName: row.employee.firstName,
        lastName: row.employee.lastName,
        position: mappedPositions[row.positionId]?.label || '',
        shiftNotes: row.comments || '',
        tags: row.tags ? row.tags.map(({ id }) => mappedTags[id]?.label).join(', ') : '',
        in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
        out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
        checkIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
        checkOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
        totalHours: Number(moment.duration(row.actualTotal).asHours().toFixed(3)),
        breaks: totalBreaks ? Number(moment.duration(totalBreaks).asHours().toFixed(3)) : '',
        paidHours: Number(
          moment
            .duration(hours.asMilliseconds() - totalBreaks)
            .asHours()
            .toFixed(3)
        ),
        '100%': aggregatedHours?.regular || '',
        '125%': aggregatedHours?.overtime || '',
        '150%': aggregatedHours?.overtime2 + aggregatedHours?.holidayRegular || '',
        '175%': aggregatedHours?.holidayOvertime || '',
        '200%': aggregatedHours?.holidayOvertime2 || '',
        hourlyRate: row.wage?.hourlyWage || 0,
        payrollNotes: row.payrollNotes || '',
        total: row.payroll?.value ? Number(row.payroll.value.toFixed(2)) : ''
      },
      columns
    );
  });

  exportExcel({
    baseData: visibleData || [],
    name: [accountName, t('detailedWithSummary')].join(' - '),
    title: '',
    detailedTable: null,
    showTitle: false
  });
};

const generalPayrollDetailedWithSummaries = ({
  groupedData,
  employeesMap,
  t,
  dateFormat,
  timeFormat,
  mappedTags,
  firstDayOfWeek,
  dateSorter,
  mappedPositions,
  mappedLocations = {},
  accountName,
  timesheetFields = []
}) => {
  const groupedDataSorted = groupedData.sort((a, b) => SORT_BY_NAME(a.subRows?.[0], b.subRows?.[0]));
  groupedDataSorted.forEach(({ subRows }) => subRows.sort(dateSorter));

  groupedDataSorted.forEach((data) => {
    const { groupValue, actualTotal, aggregatedHoursTotalRow, subRows, payrollTotal, clientWageTotal } = data;

    const { totalBreaks } = subRows.reduce(
      ({ totalBreaks: oldTotalBreaks }, row) => {
        const { breaktimes = [] } = row;

        return {
          totalBreaks:
            oldTotalBreaks + breaktimes.reduce((sum, { startTime, endTime }) => sum + totalTime(startTime, endTime), 0)
        };
      },
      { totalBreaks: 0 }
    );

    const employee = employeesMap[groupValue];
    const employeeIdField = isNaN(employee.identification) ? employee.identification : Number(employee.identification);

    const { regular, overtime, overtime2, holidayRegular, holidayOvertime, holidayOvertime2 } = aggregatedHoursTotalRow;
    data.subRows.push({
      summary: true,
      id: employeeIdField || '',
      firstName: employee.firstName,
      lastName: employee.lastName,
      actualTotal,
      regular,
      holidayRegular,
      holidayOvertime,
      overtime,
      holidayOvertime2,
      overtime2,
      totalShifts: subRows.length,
      totalBreaks,
      payrollTotal,
      clientWageTotal
    });
  });

  const groupedDataSummarized = groupedDataSorted.reduce(
    (prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))],
    []
  );

  // Create timesheet field column names
  const timesheetFieldColumnNames = (timesheetFields || []).map(({ name, isDeleted }) =>
    isDeleted ? `${name} (${t('deleted')})` : name
  );

  const columns = [
    'employee-id',
    'date',
    'day',
    'type',
    'firstName',
    'lastName',
    'position',
    'location',
    'address',
    'shiftNotes',
    'tags',
    'in',
    'out',
    'totalHours',
    'breaks',
    'paidHours',
    'regular',
    'overtime',
    'overtime2',
    'holidayRegular',
    'holidayOvertime',
    'holidayOvertime2',
    'hourlyRate',
    'payrollNotes',
    'totalPayroll',
    'sequencesResultsCompletedFieldsStatus',
    ...timesheetFieldColumnNames
  ].map((column) => ({ Header: t(columnHeaderKey(column)), dataKey: column }));

  const visibleData = groupedDataSummarized.map((row) => {
    if (row.summary) {
      const {
        id: employeeIdField,
        firstName,
        lastName,
        totalShifts,
        regular,
        holidayRegular,
        holidayOvertime,
        overtime,
        totalBreaks,
        actualTotal,
        holidayOvertime2,
        overtime2,
        payrollTotal
      } = row;

      const paidHours = actualTotal - (totalBreaks || 0);

      return conditionalColumns(
        {
          'employee-id': employeeIdField,
          date: totalShifts,
          day: '',
          type: `${t('monthlyCalculation')} : ${t('decimal')}`,
          firstName,
          lastName,
          position: '',
          location: '',
          address: '',
          shiftNotes: '',
          tags: '',
          in: '',
          out: '',
          checkIn: '',
          checkOut: '',
          totalHours: actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(2)) : '',
          breaks: totalBreaks > 0 ? Number(moment.duration(totalBreaks).asHours().toFixed(2)) : '',
          paidHours: paidHours ? Number(moment.duration(paidHours).asHours().toFixed(2)) : '',
          regular: regular ? Number(regular).toFixed(2) : '',
          overtime: overtime ? Number(overtime).toFixed(2) : '',
          overtime2: overtime2 ? Number(overtime2).toFixed(2) : '',
          holidayRegular: holidayRegular ? Number(holidayRegular).toFixed(2) : '',
          holidayOvertime: holidayOvertime ? Number(holidayOvertime).toFixed(2) : '',
          holidayOvertime2: holidayOvertime2 ? Number(holidayOvertime2).toFixed(2) : '',
          hourlyRate: '',
          payrollNotes: '',
          totalPayroll: payrollTotal || ''
        },
        columns
      );
    }

    const totalBreaks = (row.breaktimes || []).reduce(
      (sum, { startTime, endTime }) => sum + totalTime(startTime, endTime),
      0
    );

    const isWeekend = () => {
      const dayOfWeek = moment(row.shiftDate, API_DATE_TIME_FORMAT).day();
      if (firstDayOfWeek === 1) {
        return dayOfWeek === 5 || dayOfWeek === 6; // sunday - 0
      }
      if (firstDayOfWeek === 2) {
        return dayOfWeek === 0 || dayOfWeek === 6;
      }
      return false;
    };

    const hours =
      row.actualOut && row.actualIn
        ? moment.duration(moment(row.actualOut).diff(moment(row.actualIn)))
        : moment.duration(0);

    const { aggregatedHours } = row.payroll || {};

    const employeeID = isNaN(row.employee.identification)
      ? row.employee.identification
      : Number(row.employee.identification);

    const selectedDate = row.shiftDate || row.date;

    // every new column added here needs to be added in 'columns' array and in row.summary
    return conditionalColumns(
      {
        'employee-id': employeeID || '',
        date: moment(selectedDate, API_DATE_TIME_FORMAT).format(dateFormat),
        day: moment(selectedDate, API_DATE_TIME_FORMAT).format('dddd'),
        type: isWeekend() ? t('weekend') : t('weekday'),
        firstName: row.employee.firstName,
        lastName: row.employee.lastName,
        position: mappedPositions[row.positionId]?.label || '',
        location: row.locationId ? mappedLocations[row.locationId]?.name || '' : '',
        address: row.locationId ? mappedLocations[row.locationId]?.address || '' : '',
        shiftNotes: row.comments || '',
        tags: row.tags ? row.tags.map(({ id }) => mappedTags[id]?.label).join(', ') : '',
        in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
        out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
        checkIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
        checkOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
        totalHours: Number(moment.duration(row.actualTotal).asHours().toFixed(3)),
        breaks: totalBreaks ? Number(moment.duration(totalBreaks).asHours().toFixed(3)) : '',
        paidHours: Number(
          moment
            .duration(hours.asMilliseconds() - totalBreaks)
            .asHours()
            .toFixed(3)
        ),
        regular: aggregatedHours?.regular || '',
        overtime: aggregatedHours?.overtime || '',
        overtime2: aggregatedHours?.overtime2 || '',
        holidayRegular: aggregatedHours?.holidayRegular || '',
        holidayOvertime: aggregatedHours?.holidayOvertime || '',
        holidayOvertime2: aggregatedHours?.holidayOvertime2 || '',

        hourlyRate: row.wage?.hourlyWage || 0,
        payrollNotes: row.payrollNotes || '',
        totalPayroll: row.payroll?.value ? Number(row.payroll.value.toFixed(2)) : ''
      },
      columns
    );
  });

  exportExcel({
    baseData: visibleData || [],
    name: [accountName, t('detailedWithSummary')].join(' - '),
    title: '',
    detailedTable: null,
    showTitle: false
  });
};

export const generateClientCSV = ({
  columns,
  accountId,
  t,
  mappedClientBranches,
  mappedClients,
  employeesMap,
  mapPayroll,
  from,
  to,
  handleError,
  additionalRevenues,
  slots,
  fields,
  dateFormat,
  timeFormat
}) => {
  const groupedData = groupByProjectData({
    employeesMap,
    slots,
    mapPayroll,
    groupByKey: 'employee',
    mappedClientBranches,
    byClient: true,
    summarized: false
  });
  if (groupedData?.length <= 0 && handleError) {
    handleError(t('noDataToExport'));
    return;
  }

  const visibleData = groupedData.map((data) => {
    const {
      groupValue,
      shift,
      actualTotal,
      payrollTotal,
      aggregatedHoursTotalRow,
      subRows,
      fields: rowFields,
      fieldsTotal
    } = data;

    const approvedByClient = subRows?.[0]?.clientApproved;

    const approvedByClientData =
      subRows?.[0]?.clientBranchId &&
      subRows?.[0]?.clientApproved &&
      approvedByClient?.adminId &&
      mappedClientBranches[subRows?.[0]?.clientBranchId]?.admins?.find(({ id }) => id === approvedByClient?.adminId);

    const employeeID = isNaN(employeesMap[groupValue].identification)
      ? employeesMap[groupValue].identification
      : Number(employeesMap[groupValue].identification);

    return conditionalColumns(
      {
        'employee-id': employeeID || '',
        firstName: employeesMap[groupValue].firstName,
        lastName: employeesMap[groupValue].lastName,
        numOfShifts: shift,
        totalHours: actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(2)) : '',

        clientApprovedBy: approvedByClientData
          ? `${approvedByClientData.firstName} ${approvedByClientData.lastName}`
          : '',
        clientApprovedAt: approvedByClient
          ? `${moment(approvedByClient.createdAt).format(dateFormat)} ${moment(approvedByClient.createdAt).format(
              timeFormat
            )}`
          : '',
        hourlyRate: subRows?.[0].wage?.hourlyWage || 0,
        flatWage: subRows?.[0].wage?.flatWage || 0,
        flatHours: subRows?.[0].wage?.flatHours || 0,
        clientWage: subRows?.[0].clientWage?.hourlyWage || 0,
        ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
          (prev, key) => ({ ...prev, [key]: aggregatedHoursTotalRow?.[key] || '' }),
          {}
        ),
        ...(fields || []).reduce((output, current, index) => {
          const slug = current.slug || `other ${index}`;
          return { ...output, [slug]: rowFields[current.id] || '' };
        }, {}),
        ...(fields || []).reduce((output, current, index) => {
          const slug = `${current.slug} ${t('total')}` || `other ${index}`;
          return { ...output, [slug]: fieldsTotal[current.id] || '' };
        }, {}),
        total: payrollTotal || '',
        invoiceNumber: subRows[0]?.invoiceNumber,
        clientName: mappedClients[subRows[0]?.clientBranch?.id]?.name || '',
        shiftsDates: [...new Set(subRows.map(({ date }) => moment(date).format(dateFormat)))].join(', ')
      },
      columns
    );
  });

  const payrollTotal = (groupedData || []).reduce((all, curr) => all + curr?.payrollTotal || 0, 0);

  const emptyColumns = Object.keys(visibleData[0]).reduce((all, key) => {
    all[key] = '';
    return all;
  }, {});

  let agencyFee22Value = 0;
  let insuranceFee2Value = 0;
  let totalIncluded = payrollTotal + additionalRevenues;

  if (accountId === PRICELESS_ONE_ACCOUNT_ID) {
    agencyFee22Value = (22 / 100) * payrollTotal;
    insuranceFee2Value = (2 / 100) * payrollTotal;
    totalIncluded += agencyFee22Value + insuranceFee2Value;
  }

  visibleData.push(
    ...[
      ...(accountId === PRICELESS_ONE_ACCOUNT_ID
        ? [
            { 'employee-id': t('Agency fee 22%'), total: agencyFee22Value },
            { 'employee-id': t('Insurance fee 2%'), total: insuranceFee2Value }
          ]
        : []),
      {
        'employee-id': t('total'),
        total: payrollTotal
      },
      { 'employee-id': t('additionalCharges'), total: additionalRevenues },
      { 'employee-id': t('totalIncluded'), total: totalIncluded }
    ]
      .map((obj) => conditionalColumns(obj, columns))
      .map((obj) => ({ ...emptyColumns, ...obj }))
  );

  exportExcel({ baseData: visibleData || [], name: `${t('clientInvoice')} ${from} - ${to}`, showTitle: false });
};

export const generateOBClientInvoiceCSV = async ({
  mappedClients,
  mappedLocations,
  mappedAdmins,
  mappedClientBranches,
  mappedPositions,
  logo,

  employeesMap,
  mapPayroll,

  from,
  to,

  slots,

  bookerId,
  clientAdminId,
  clientId,
  invoiceNumber,
  locationId,
  name
}) => {
  const groupedData = groupByProjectData({
    employeesMap,
    slots,
    mapPayroll,
    groupByKey: 'employee',
    mappedClientBranches,
    byClient: true,
    summarized: false
  });

  // generate all the dates between the Monday before the from until the Sunnday after to
  const allDatesOptions = generateRange(
    {
      start: moment(from, API_DATE_FORMAT).startOf('week').format(API_DATE_FORMAT),
      end: moment(to, API_DATE_FORMAT).endOf('week').format(API_DATE_FORMAT)
    } || []
  ).map((date) => {
    const formattedDate = moment(date).format(API_DATE_FORMAT);
    const formattedDay = moment(date).format('ddd');

    return { date: formattedDate, day: formattedDay, dayNumber: moment(date).day() };
  });
  const isEventCrossWeek = allDatesOptions.length > 7;
  const formatDayKey = ({ day, date }) => (isEventCrossWeek ? `${day}-${date}` : day);

  const employeePosition = {};

  const overtimeMultiplier = 0.35;
  const overtime2Multiplier = 0.75;

  const roundUpTwoDigits = (number) => Math.ceil(number * 100) / 100;

  groupedData.forEach(({ subRows }) => {
    subRows.forEach(({ positionId, actualIn, actualTotal, employee, payroll = {} }) => {
      if (!actualIn) return;

      const actuaTotalInHours = actualTotal / (60 * 60 * 1000);

      const employeeId = employee.id;
      const { overtime = 0, overtime2 = 0 } = payroll.aggregatedHours || {};
      const { hourlyWage = 0 } = payroll.wage || {};

      const workingDate = actualIn ? moment(actualIn).format(API_DATE_FORMAT) : null;
      const positionKey = positionId || -1;

      if (!employeePosition[employeeId]) {
        employeePosition[employeeId] = {};
      }

      if (!employeePosition[employeeId][positionKey]) {
        employeePosition[employeeId][positionKey] = {
          allHours: {
            Name: [employeesMap[employeeId].firstName, employeesMap[employeeId].lastName]
              .filter((curr) => !!curr)
              .join(' '),
            Number: employee.employeeIdField || '',
            Trade: positionId ? mappedPositions[positionId]?.label : '',
            HoursType: 'Hours Worked',
            ...(allDatesOptions || []).reduce((output, { date, day }) => {
              const formattedKey = formatDayKey({ date, day });
              output[formattedKey] = '';
              return output;
            }, {}),
            connected: false,
            showRow: true,
            Total: 0,
            Rate: hourlyWage,
            Amount: 0
          },
          overtime: {
            Name: '',
            Number: '',
            Trade: '',
            HoursType: 'O/T Factor 0.35',
            ...(allDatesOptions || []).reduce((output, { date, day }) => {
              const formattedKey = formatDayKey({ date, day });
              output[formattedKey] = '';
              return output;
            }, {}),
            connected: true,
            showRow: true,
            Total: 0,
            Rate: hourlyWage,
            Amount: 0
          },
          overtime2: {
            Name: '',
            Number: '',
            Trade: '',
            HoursType: 'O/T Factor 0.75',
            ...(allDatesOptions || []).reduce((output, { date, day }) => {
              const formattedKey = formatDayKey({ date, day });
              output[formattedKey] = '';
              return output;
            }, {}),
            connected: true,
            showRow: true,
            Total: 0,
            Rate: hourlyWage,
            Amount: 0
          }
        };
      }

      (allDatesOptions || []).forEach(({ date, day }) => {
        if (date !== workingDate) {
          return;
        }
        const formattedKey = formatDayKey({ date, day });

        employeePosition[employeeId][positionKey].allHours[formattedKey] = actuaTotalInHours;
        employeePosition[employeeId][positionKey].allHours.Total += actuaTotalInHours;

        if (overtime) {
          const overtimeResult = roundUpTwoDigits(overtime * overtimeMultiplier);
          employeePosition[employeeId][positionKey].overtime[formattedKey] = overtimeResult;
          employeePosition[employeeId][positionKey].overtime.Total += overtimeResult;
        }

        if (overtime2) {
          const overtime2Result = roundUpTwoDigits(overtime2 * overtime2Multiplier);
          employeePosition[employeeId][positionKey].overtime2[formattedKey] = overtime2Result;
          employeePosition[employeeId][positionKey].overtime2.Total += overtime2Result;
        }
      });

      employeePosition[employeeId][positionKey].overtime.showRow =
        employeePosition[employeeId][positionKey].overtime.Total !== 0;
      employeePosition[employeeId][positionKey].overtime2.showRow =
        employeePosition[employeeId][positionKey].overtime2.Total !== 0;

      employeePosition[employeeId][positionKey].allHours.Amount =
        hourlyWage * employeePosition[employeeId][positionKey].allHours.Total;

      employeePosition[employeeId][positionKey].overtime.Amount =
        hourlyWage * employeePosition[employeeId][positionKey].overtime.Total;
      employeePosition[employeeId][positionKey].overtime2.Amount =
        hourlyWage * employeePosition[employeeId][positionKey].overtime2.Total;
    });
  });

  const visibleData = []
    .concat(
      ...Object.values(employeePosition).map((obj) =>
        Object.values(obj).map((item) => Object.values(item).map((innerItem) => innerItem))
      )
    )
    .flat();

  const total = visibleData.reduce((all, { Amount }) => all + Amount || 0, 0);

  const datesArray = ['', '', '', '', '', ...allDatesOptions.map(({ date }) => moment(date).format('DD/MM'))];
  const bookerName = [mappedAdmins[bookerId]?.firstName, mappedAdmins[bookerId]?.lastName]
    .filter((curr) => !!curr)
    .join(' ');

  const clientContact = clientAdminId ? mappedClients[clientId].admins.find(({ id }) => id === clientAdminId) : '';

  const generalInfo = {
    eventName: name,
    clientName: mappedClients[clientId]?.name || '',
    locationName: mappedLocations[locationId].name || '',
    bookerName,
    clientContactName: clientContact
      ? [clientContact.firstName, clientContact.lastName].filter((curr) => !!curr).join(' ')
      : '',
    lastSundayOfWeek: moment(allDatesOptions[allDatesOptions.length - 1].date, API_DATE_FORMAT).format('DD/MM/YYYY'),
    invoiceNumber
  };

  return exportExcelOB({
    baseData: visibleData || [],
    datesArray,
    generalInfo,
    summary: { total },
    isEmptyExport: false,
    logo,
    name: `O&B weekly invoice details - ${moment(allDatesOptions[0].date).format('DD-MM')} - ${moment(
      allDatesOptions[allDatesOptions.length - 1].date
    ).format('DD-MM')}`
  });
};
export const generateOBClientInvoiceCSVEmpty = async ({
  mappedClients,
  mappedLocations,
  mappedAdmins,
  mappedClientBranches,
  mappedPositions,
  logo,

  employeesMap,
  mapPayroll,

  from,
  to,

  slots,

  bookerId,
  clientAdminId,
  clientId,
  invoiceNumber,
  locationId,
  name
}) => {
  const groupedData = groupByProjectData({
    employeesMap,
    slots,
    mapPayroll,
    groupByKey: 'employee',
    mappedClientBranches,
    byClient: true,
    summarized: false
  });

  // generate all the dates between the Monday before the from until the Sunnday after to
  const allDatesOptions = generateRange(
    {
      start: moment(from, API_DATE_FORMAT).startOf('week').format(API_DATE_FORMAT),
      end: moment(to, API_DATE_FORMAT).endOf('week').format(API_DATE_FORMAT)
    } || []
  ).map((date) => {
    const formattedDate = moment(date).format(API_DATE_FORMAT);
    const formattedDay = moment(date).format('ddd');

    return { date: formattedDate, day: formattedDay, dayNumber: moment(date).day() };
  });
  const isEventCrossWeek = allDatesOptions.length > 7;

  const formatDayKey = ({ day, date }) => (isEventCrossWeek ? `${day}-${date}` : day);
  const employeePosition = {};

  groupedData.forEach(({ subRows }) => {
    subRows.forEach(({ positionId, employee }) => {
      const employeeId = employee.id;

      const positionKey = positionId || -1;

      if (!employeePosition[employeeId]) {
        employeePosition[employeeId] = {};
      }

      if (!employeePosition[employeeId][positionKey]) {
        employeePosition[employeeId][positionKey] = {
          Name: [employeesMap[employeeId].firstName, employeesMap[employeeId].lastName]
            .filter((curr) => !!curr)
            .join(' '),
          Number: employee.employeeIdField || '',
          Trade: positionId ? mappedPositions[positionId]?.label : '',
          ...(allDatesOptions || []).reduce((output, { date, day }) => {
            const formattedKey = formatDayKey({ date, day });

            output[formattedKey] = '';
            return output;
          }, {}),

          Total: '',
          Notes: ''
        };
      }
    });
  });

  const visibleData = Object.values(employeePosition).reduce((output, item) => {
    output.push(...Object.values(item));
    return output;
  }, []);

  const datesArray = ['', '', '', '', ...allDatesOptions.map(({ date }) => moment(date).format('DD/MM'))];
  const bookerName = [mappedAdmins[bookerId]?.firstName, mappedAdmins[bookerId]?.lastName]
    .filter((curr) => !!curr)
    .join(' ');

  const clientContact = clientAdminId ? mappedClients[clientId].admins.find(({ id }) => id === clientAdminId) : '';

  const generalInfo = {
    eventName: name,
    clientName: mappedClients[clientId]?.name || '',
    locationName: mappedLocations[locationId].name || '',
    bookerName,
    clientContactName: clientContact
      ? [clientContact.firstName, clientContact.lastName].filter((curr) => !!curr).join(' ')
      : '',
    lastSundayOfWeek: moment(allDatesOptions[allDatesOptions.length - 1].date, API_DATE_FORMAT).format('DD/MM/YYYY'),
    invoiceNumber
  };

  return exportExcelOB({
    baseData: visibleData.sort(SORT_BY_STRING('Trade')) || [],
    logo,
    datesArray,
    generalInfo,
    isEmptyExport: true,
    name: `O&B weekly timesheet (empty) - ${moment(allDatesOptions[0].date).format('DD-MM')} - ${moment(
      allDatesOptions[allDatesOptions.length - 1].date
    ).format('DD-MM')}`
  });
};

export const generateProjectsClientCSV = ({
  groupedData,
  accountId,
  t,
  filePrefix,
  fields,
  timeFormat,
  dateFormat,
  mappedPositions,
  mappedSupplierPositions,
  mappedLocations,
  countryCode
}) => {
  const dateSorter = SORT_BY_DATE('date');

  const generateDataWithoutSummary = generateProjectsClientDataWithoutSummary({
    t,
    fields,
    timeFormat,
    mappedPositions,
    mappedSupplierPositions,
    groupedData,
    mappedLocations,
    dateSorter
  });

  const generateDataWithSummary = generateProjectsClientDataWithSummary({
    t,
    accountId,
    fields,
    timeFormat,
    dateFormat,
    mappedPositions,
    mappedSupplierPositions,
    countryCode,
    groupedData,
    mappedLocations,
    dateSorter
  });

  clientInvoicesExcel({
    name: [filePrefix, t('clientInvoices')].filter(Boolean).join(' - '),
    firstReport: { data: generateDataWithSummary, title: '' },
    secondReport: { data: generateDataWithoutSummary, title: '' }
  });
};

const getRowBreaks = (breaktimes) => {
  const totalBreaks = (breaktimes || [])
    .filter(
      ({ startTime, endTime }) => startTime && moment(startTime).isValid() && endTime && moment(endTime).isValid()
    )
    .reduce((sum, { startTime, endTime }) => sum + totalTime(startTime, endTime), 0);

  return totalBreaks > 0 ? getDurationFromMilliseconds(totalBreaks) : '';
};

const getRowClientStatus = ({ clientApproved, clientDeclined }, t) => {
  if (clientApproved) {
    return t('approved');
  }

  if (clientDeclined) {
    return t('declined');
  }

  return '';
};

const generateProjectsClientDataWithoutSummary = ({
  t,
  fields,
  timeFormat,
  dateSorter,
  mappedPositions,
  mappedLocations,
  mappedSupplierPositions,
  groupedData
}) => {
  const columns = [
    'name',
    'event',
    'shift',
    'shiftArea',
    'in',
    'out',
    'clientWage',
    'totalHours',
    ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY,
    'breaks',
    'clientStatus',
    ...fields.map(({ slug }) => slug),
    ...fields
      .filter(({ type }) => type === PAYROLL_CUSTOM_FIELD_TYPE.HOURS_RELATED)
      .map(({ slug }) => `${slug} ${t('total')}`),
    'total',
    'invoiceNumber'
  ].map((column) => ({ Header: t(columnHeaderKey(column)), dataKey: column }));

  const groupedDataSorted = groupedData
    .filter(({ subRows }) => subRows.some(({ payroll }) => !!payroll))
    .sort(dateSorter);

  const formattedGroupedDataSorted = groupedDataSorted.reduce(
    (prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))],
    []
  );

  const visibleData = formattedGroupedDataSorted.map((row) => {
    const { aggregatedHours } = row.payroll || {};

    const shiftData = `${
      row.positionId
        ? `${mappedPositions[row.positionId]?.label || mappedSupplierPositions[row.positionId]?.label} | `
        : ''
    }${moment(row.scheduleIn).format(timeFormat)} ${
      row.scheduleOut ? `- ${moment(row.scheduleOut).format(timeFormat)}` : ''
    }${row.comments ? ` | ${row.comments}` : ''}`;

    return conditionalColumns(
      {
        name: [row.employee?.firstName, row.employee?.lastName].join(' '),
        event: row.groupValue,
        shift: shiftData,
        shiftArea: mappedLocations[row?.locationId]?.name || '',
        in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
        out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
        checkIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
        checkOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
        clientWage: row?.payroll?.wage?.hourlyWage || 0,
        totalHours: Number(moment.duration(row.actualTotal).asHours().toFixed(3)),
        ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
          (prev, key) => ({ ...prev, [key]: aggregatedHours?.[key] || '' }),
          {}
        ),
        breaks: getRowBreaks(row.breaktimes),
        clientStatus: getRowClientStatus(row, t),
        ...(fields || []).reduce((output, current, index) => {
          const slug = current.slug || `other ${index}`;
          return {
            ...output,
            [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.value || ''
          };
        }, {}),
        ...(fields || []).reduce((output, current, index) => {
          const slug = `${current.slug} ${t('total')}` || `other ${index}`;
          return {
            ...output,
            [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.total || ''
          };
        }, {}),
        total: row.payroll?.value ? Number(row.payroll.value.toFixed(2)) : '',
        invoiceNumber: row?.invoiceNumber
      },
      columns
    );
  });

  return visibleData;
};

const generateProjectsClientDataWithSummary = ({
  t,
  accountId,
  fields,
  timeFormat,
  dateFormat,
  dateSorter,
  groupedData,
  mappedPositions,
  mappedSupplierPositions,
  mappedLocations,
  countryCode
}) => {
  const columns = [
    'name',
    'shift',
    'date',
    'shiftArea',
    'in',
    'out',
    'clientWage',
    'totalHours',
    ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY,
    'breaks',
    'clientStatus',
    ...fields.map(({ slug }) => slug),
    ...fields
      .filter(({ type }) => type === PAYROLL_CUSTOM_FIELD_TYPE.HOURS_RELATED)
      .map(({ slug }) => `${slug} ${t('total')}`),
    'total',
    'invoiceNumber'
  ].map((column) => ({ Header: t(columnHeaderKey(column)), dataKey: column }));

  const groupedDataSorted = groupedData
    .filter(({ subRows }) => subRows.some(({ payroll }) => !!payroll))
    .sort(dateSorter);

  const columnsRow = columns.reduce((all, column) => {
    all[column.dataKey] = column.Header;
    return all;
  }, {});

  const groupedDataWithSummaryRows = groupedDataSorted.map((data) => {
    const {
      groupValue,
      additionalRevenues,
      actualTotal,
      fields: rowFields,
      aggregatedHoursTotalRow,
      subRows,
      fieldsTotal,
      payrollTotal,
      payrollTotalWithVat
    } = data;

    const { regular, overtime, overtime2, holidayRegular, holidayOvertime, holidayOvertime2 } = aggregatedHoursTotalRow;

    const summaryFields = {
      summary: true,
      groupValue,
      payrollTotalWithVat,
      additionalRevenues,
      actualTotal,
      regular,
      rowFields,
      holidayRegular,
      holidayOvertime,
      overtime,
      holidayOvertime2,
      overtime2,
      totalShifts: subRows.length,
      payrollTotal,
      aggregatedHoursTotalRow,
      fieldsTotal
    };

    return {
      ...data,
      subRows: [
        { ...summaryFields, projectDataSummary: true },
        { summary: true, columnsRowSummary: true },
        ...subRows,
        { ...summaryFields, withAdditionalRevenues: true },
        summaryFields,
        { ...summaryFields, withVat: true },
        { ...summaryFields, withVatSummary: true }
      ]
    };
  });

  const groupedDataSummarized = groupedDataWithSummaryRows.reduce(
    (prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))],
    []
  );

  return groupedDataSummarized.map((row) => {
    if (row.summary) {
      if (row.columnsRowSummary) {
        return columnsRow;
      }

      const {
        groupValue,
        payrollTotalWithVat,
        additionalRevenues,
        actualTotal,
        rowFields,
        totalShifts,
        payrollTotal,
        aggregatedHoursTotalRow,
        fieldsTotal
      } = row;

      let summaryData = {
        name: t('total'),
        shift: totalShifts,
        date: '',
        shiftArea: '',
        in: '',
        out: '',
        checkIn: '',
        checkOut: '',
        clientWage: '',
        totalHours: actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(3)) : 0,
        ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
          (prev, key) => ({ ...prev, [key]: aggregatedHoursTotalRow?.[key] || '' }),
          {}
        ),
        breaks: '',
        clientStatus: '',
        ...(fields || []).reduce((output, current, index) => {
          const slug = current.slug || `other ${index}`;
          return { ...output, [slug]: rowFields[current.id] || '' };
        }, {}),
        ...(fields || []).reduce((output, current, index) => {
          const slug = `${current.slug} ${t('total')}` || `other ${index}`;
          return { ...output, [slug]: fieldsTotal[current.id] || '' };
        }, {}),
        total: payrollTotal + (additionalRevenues || 0) || '',
        invoiceNumber: ''
      };

      if (row.withAdditionalRevenues) {
        summaryData = columns.reduce((all, { dataKey }) => {
          all[dataKey] = dataKey === 'total' ? additionalRevenues : dataKey === 'name' ? t('additionalRevenues') : '';

          return all;
        }, {});
      }

      if (row.projectDataSummary) {
        summaryData = columns.reduce((all, { dataKey }) => {
          all[dataKey] = dataKey === 'name' ? groupValue : '';
          return all;
        }, {});
      }

      if (row.withVat) {
        summaryData = columns.reduce((all, { dataKey }) => {
          all[dataKey] =
            dataKey === 'total'
              ? accountId === LABOR_DESK_ACCOUNT_ID
                ? HARD_CODED_LABOR_DESK_VAT_PRECENTAGE
                : HARD_CODED_ISRAEL_VAT_PRECENTAGE
              : dataKey === 'name'
              ? t('vatPercentage')
              : '';

          return all;
        }, {});
      }

      if (row.withVatSummary) {
        /* If israeli team - use hard coded vat - payrollTotalWithAdditionalRevenuesIsraelVAT.
        else, use the pre-calculated payrollTotalWithVat */
        const payrollTotalWithAdditionalRevenuesIsraelVAT =
          (payrollTotal + additionalRevenues) *
          (accountId === LABOR_DESK_ACCOUNT_ID ? HARD_CODED_LABOR_DESK_VAT : HARD_CODED_ISRAEL_VAT);

        /* TODO : no support for additionalRevenues in non israeli groups */

        const total =
          countryCode?.toLowerCase() === 'il' || accountId === LABOR_DESK_ACCOUNT_ID
            ? payrollTotalWithAdditionalRevenuesIsraelVAT
            : payrollTotalWithVat;
        summaryData = columns.reduce((all, { dataKey }) => {
          all[dataKey] = dataKey === 'total' ? total : dataKey === 'name' ? t('totalWithVAT') : '';

          return all;
        }, {});
      }
      return conditionalColumns(summaryData, columns);
    }

    const { aggregatedHours } = row.payroll || {};

    const shiftData = `${
      row.positionId
        ? `${mappedPositions[row.positionId]?.label || mappedSupplierPositions[row.positionId]?.label} | `
        : ''
    }${moment(row.scheduleIn).format(timeFormat)} ${
      row.scheduleOut ? `- ${moment(row.scheduleOut).format(timeFormat)}` : ''
    }${row.comments ? ` | ${row.comments}` : ''}`;

    return conditionalColumns(
      {
        name: [row.employee?.firstName, row.employee?.lastName].join(' ') || '',
        shift: shiftData,
        date: row.date ? moment(row.date).format(dateFormat) : '',
        shiftArea: mappedLocations[row?.locationId]?.name || '',
        in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
        out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
        checkIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
        checkOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
        clientWage: row?.payroll?.wage?.hourlyWage || 0,
        totalHours: Number(moment.duration(row.actualTotal).asHours().toFixed(3)),
        ...EXTRA_PAYROLL_COLUMNS_WITH_HOLIDAY.reduce(
          (prev, key) => ({ ...prev, [key]: aggregatedHours?.[key] || '' }),
          {}
        ),
        breaks: getRowBreaks(row.breaktimes),
        clientStatus: getRowClientStatus(row, t),
        ...(fields || []).reduce((output, current, index) => {
          const slug = current.slug || `other ${index}`;
          return {
            ...output,
            [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.value || ''
          };
        }, {}),
        ...(fields || []).reduce((output, current, index) => {
          const slug = `${current.slug} ${t('total')}` || `other ${index}`;
          return {
            ...output,
            [slug]: row.payroll?.fields.find((field) => field.id === current.id)?.total || ''
          };
        }, {}),
        total: row.payroll?.value ? Number(row.payroll.value.toFixed(2)) : '',
        invoiceNumber: row?.invoiceNumber
      },
      columns
    );
  });
};

export const generateSummarizedClientInvoiceReportCSV = ({
  projects,
  t,
  accountId,
  includeVAT,
  timeRange,
  shortDateFormat,
  filePrefix
}) => {
  const dateSorter = SORT_BY_DATE('projectDate');

  const columns = [
    'date',
    'invoiceNumber',
    'name',
    'employees',
    'baseTotal',
    'additionalRevenues',
    'total',
    ...(includeVAT ? ['vatPercentage', 'totalWithVAT'] : [])
  ].map((column) => ({ Header: t(column), dataKey: column }));

  const visibleData = (projects || [])
    .sort(dateSorter)
    .map(({ projectDate, projectName, employees, additionalRevenues, total, totalWithAdditional, invoiceNumber }) => {
      return conditionalColumns(
        {
          date: moment(projectDate).format(shortDateFormat),
          invoiceNumber: invoiceNumber || '',
          name: projectName || '',
          employees: employees || 0,
          baseTotal: total || '',
          additionalRevenues: additionalRevenues || '',
          total: totalWithAdditional || '',

          ...(includeVAT ? ['vatPercentage', 'totalWithVAT'] : []).reduce(
            (prev, key) => ({
              ...prev,
              [key]:
                key === 'vatPercentage'
                  ? accountId === LABOR_DESK_ACCOUNT_ID
                    ? HARD_CODED_LABOR_DESK_VAT_PRECENTAGE
                    : HARD_CODED_ISRAEL_VAT_PRECENTAGE
                  : totalWithAdditional
                  ? (totalWithAdditional || 0) *
                    (accountId === LABOR_DESK_ACCOUNT_ID ? HARD_CODED_LABOR_DESK_VAT : HARD_CODED_ISRAEL_VAT)
                  : ''
            }),
            {}
          )
        },
        columns
      );
    });

  const startDate = timeRange.start.format(API_DATE_FORMAT);

  const endDate = timeRange.end.format(API_DATE_FORMAT);

  const fileName = [filePrefix, startDate, endDate].filter(Boolean).join(' - ');

  exportExcel({ baseData: visibleData || [[]], name: fileName, showTitle: false });
};

export const generateXEROReportCSV = ({
  mappedClients,
  timesheetData,
  clientMapPayroll,
  mappedLocations,
  mappedLabels,
  shortDateFormat,
  t,
  search,
  filters,
  from,
  to
}) => {
  const groupedData = groupByData({
    jobs: [],
    slots: timesheetData?.slots?.filter(({ clientId }) => !!clientId),
    employees: timesheetData?.employees,
    groupByKey: 'project',
    clientMapPayroll,
    summarized: false,
    search,
    filters,
    mappedLocations,
    mappedLabels
  });

  const projects = groupedData.map((item) => {
    const subRow = item.subRows[0];

    const { additionalRevenues, clientWageTotal } = item;

    return {
      clientId: subRow.clientId,
      projectEndDate: subRow.endDate,
      projectName: subRow.name || mappedLocations[subRow.locationId]?.name,
      totalWithAdditional: additionalRevenues + clientWageTotal,
      invoiceNumber: subRow.invoiceNumber
    };
  });

  return generateXEROClientInvoiceReportCSV({
    projects,
    t,
    mappedClients,
    shortDateFormat,
    fileName: `${t('xeroReport')} ${from} - ${to}`
  });
};

export const generateXEROClientInvoiceReportCSV = ({ projects, t, fileName, mappedClients, shortDateFormat }) => {
  const dateSorter = SORT_BY_DATE('projectDate');

  const columns = [
    '*ContactName',
    'EmailAddress',
    'POAddressLine1',
    'POAddressLine2',
    'POAddressLine3',
    'POAddressLine4',
    'POCity',
    'PORegion',
    'POPostalCode',
    'POCountry',
    'InvoiceNumber',
    'Reference',
    'InvoiceDate',
    'DueDate',
    'Total',
    'InventoryItemCode',
    'Description',
    'Quantity',
    'UnitAmount',
    'Discount',
    'AccountCode',
    'TaxType',
    'TaxAmount',
    'TrackingName1',
    'TrackingOption1',
    'TrackingName2',
    'TrackingOption2',
    'Currency',
    'BrandingTheme'
  ].map((column) => ({ Header: t(column), dataKey: column }));

  const visibleData = (projects || [])
    .sort(dateSorter)
    .map(({ projectEndDate, projectName, clientId, totalWithAdditional, invoiceNumber }) => {
      return conditionalColumns(
        {
          '*ContactName': mappedClients[clientId]?.name || '',
          EmailAddress: '',
          POAddressLine1: '',
          POAddressLine2: '',
          POAddressLine3: '',
          POAddressLine4: '',
          POCity: '',
          PORegion: '',
          POPostalCode: '',
          POCountry: '',
          InvoiceNumber: invoiceNumber || '',
          Reference: '',
          InvoiceDate: moment().format(shortDateFormat),
          DueDate: moment(projectEndDate).format(shortDateFormat),
          Total: '',
          InventoryItemCode: '',
          Description: `${projectName ? `${projectName} -` : ''} Ordered Staff`,
          Quantity: 1,
          UnitAmount: totalWithAdditional || '',
          Discount: '',
          AccountCode: '',
          TaxType: '',
          TaxAmount: '',
          TrackingName1: '',
          TrackingOption1: '',
          TrackingName2: '',
          TrackingOption2: '',
          Currency: '',
          BrandingTheme: ''
        },
        columns
      );
    });

  exportExcel({ baseData: visibleData || [[]], name: fileName, showTitle: false });
};

export const generateCoastalReportCSV = (props) => {
  const columns = Object.entries({
    employeeId: 'Key',
    approvalStatus: '#Approval status',
    approvedBy: '#Approved by',
    approvedAt: '#Approved at',
    clientNameLaborValue: 'LaborValue4',
    firstName: '#First Name',
    lastName: '#Last Name',
    date: '#Date',
    shiftTime: '#Shift time',
    positionGroups: 'LaborValue5',
    shift: '#Shift',
    tags: '#Shift tags',
    clientName: '#Client name',
    in: '#In',
    out: '#Out',
    location: '#Location',
    regular: 'E_Hourly Regular_Hours',
    overtime: 'E_Overtime @ 1.5_Hours',
    overtime2: 'E_double time_hours',
    holidayRegular: 'E_Holiday_Hours',
    breaks: '#Breaks',
    totalHours: '#Total hours',
    hourlyRate: '#Hourly rate',
    flatWage: '#Flat rate',
    flatHours: '#Flat rate hours limit',
    clientWage: '#Bill rate',
    'Mileage Expense': 'E_Mileage - Non T_Dollars',
    'Misc Expense': 'E_Expense Reimb_Dollars',
    'Parking Expense': 'E_Park Reimb_Dollars',
    'Telephone Expense': 'E_Telephone Reimb_Dollars',
    totalPayroll: '#Total',
    invoiceNumber: '#Invoice Number',
    payrollNotes: '#Payroll notes',
    schedulingNotes: '#Scheduling notes'
  }).map(([column, label]) => ({ Header: label, dataKey: column }));

  return generateCSV({
    ...props,
    columns,
    summarized: false,
    rowTransformer: (row) => {
      const data = {
        ...row,
        employeeId: row['employee-id'],
        clientNameLaborValue: row.clientName
      };

      return Object.fromEntries(columns.map(({ dataKey }) => [dataKey, data[dataKey] || '']));
    }
  });
};

const generalPremiumEventStaff = ({
  groupedData,
  columns,
  formattingMap,
  dateFormat,
  t,
  from,
  to,
  mappedTags,
  exportTitle
}) => {
  const visibleData = groupedData.reduce(
    (prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))],
    []
  );

  const exportedData = [];

  visibleData.forEach((row) => {
    const employeeID = isNaN(row.employee.identification)
      ? row.employee.identification
      : Number(row.employee.identification);

    const totalHours =
      row.actualIn && row.actualOut
        ? formattingMap.number(
            moment
              .duration(moment(row.actualOut).diff(moment(row.actualIn)))
              .asHours()
              .toFixed(2)
          )
        : '';

    const tip = (row?.payroll?.fields || [])?.find(({ id }) => id === PAYROLL_FIELD_TIP_ID)?.value || 0;

    const pay = moment(row.scheduleIn, API_DATE_TIME_FORMAT).format('ddd');
    const date = moment(row.scheduleIn, API_DATE_TIME_FORMAT).format(dateFormat);

    exportedData.push(
      conditionalColumns(
        {
          date,
          EmployeeID: employeeID || '',
          FirstName: row.employee.firstName,
          LastName: row.employee.lastName,
          Pay: `${pay}Reg`,
          Units: totalHours,
          Hours: totalHours,
          Override: row.wage?.hourlyWage || 0,
          Dept: 'Banq',
          Div: totalHours * (row.wage?.hourlyWage || 0),
          Locn: row.tags ? row.tags.map(({ id }) => mappedTags[id]?.label).join(', ') : ''
        },
        columns
      )
    );

    if (tip > 0) {
      exportedData.push(
        conditionalColumns(
          {
            date,
            EmployeeID: employeeID || '',
            FirstName: row.employee.firstName,
            LastName: row.employee.lastName,
            Pay: `${pay}Tip`,
            Units: 1,
            Hours: 1,
            Override: tip,
            Dept: 'Banq',
            Div: tip,
            Locn: row.tags ? row.tags.map(({ id }) => mappedTags[id]?.label).join(', ') : ''
          },
          columns
        )
      );
    }
  });

  exportExcel({
    baseData: exportedData || [],
    name: `${t('detailedPayrollReport')} ${from} - ${to}`,
    showTitle: !!exportTitle,
    title: exportTitle,
    autoFilter: false
  });
};

export const generateTempStaffDetailedCSV = ({ timesheetData, mapPayroll, timeRange, search, filters, dateFormat }) => {
  if (!timeRange) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    mapPayroll,
    summarized: false,
    search,
    filters
  }).map((item) => {
    const aggregatedHoursByHourlyRate = item?.subRows.reduce((all, curr) => {
      const { wage, aggregatedHours } = curr.payroll || {};
      const { hourlyWage: currHourlyRate } = wage || {};

      if (!currHourlyRate) {
        return all;
      }

      if (!all[currHourlyRate]) {
        all[currHourlyRate] = { regular: 0, overtime: 0 };
      }

      all[currHourlyRate].regular += aggregatedHours?.regular || 0;
      all[currHourlyRate].overtime += aggregatedHours?.overtime || 0;

      return all;
    }, {});
    return { ...item, aggregatedHoursByHourlyRate };
  });

  const data = [];

  const lastDateOfTimeRange = moment(timeRange.end).format(dateFormat);

  const sharedBetweenCodesData = {
    /* hard coded */
    'Kostenstelle 1': 0,
    'Kostenstelle 2': 0,
    'Freie Nummer': 0,
    'Funktion Gemeinde': 0,
    'Art Gemeinde': 0,
    'Subnummer Referenz': 0,
    'GB zum Verbuchen der Vorerfassung': 0,
    'AnsV - Nr.': 0,
    'Wiederkehrende Vorerfassung': 'FALSE',
    'Mehrwertsteuer - Code': '',
    'Fibu - Konto': 0,
    'Freie Nummer 1': 0,
    'Freie Nummer 2': 0,
    'Freier Text 1': '',
    'Freier Text 2': '',
    'Freies Datum 1': '',
    Kommentar: '',
    Benutzername: 'Administrator',
    Mutationsdatum: ''
  };

  groupedData.forEach((item) => {
    const employeeNumberField = item.subRows[0]?.employee.identification;

    const { shift: totalShifts, aggregatedHoursByHourlyRate } = item || 0;

    (TEMP_STAFF_PAYROLL_REPORT_CODES || []).forEach(
      ({
        code,
        column: columnText,
        rate,
        mutationsCodeAnsatzValue = 1,
        isPayrollField,
        payrollFieldId,
        showTotalShifts,
        isHoursRelated = false,
        isOverTime = false
      }) => {
        let columnAnzahlValue = '';
        let columnAnsatzValue = '';

        if (isHoursRelated) {
          columnAnzahlValue = isOverTime
            ? aggregatedHoursByHourlyRate[rate]?.overtime
            : aggregatedHoursByHourlyRate[rate]?.regular;

          columnAnsatzValue = rate;
        }

        if (isPayrollField) {
          columnAnzahlValue = 1;
          columnAnsatzValue = item?.fieldsTotal?.[payrollFieldId];
        }

        if (showTotalShifts) {
          columnAnsatzValue = totalShifts;
        }

        const columnmutationsCodeAnsatzValue = mutationsCodeAnsatzValue;

        data.push({
          'Personal-Nr': employeeNumberField || '',
          Periodendatum: lastDateOfTimeRange,
          Periodennummer: 1,
          Lohnart: code,
          Belegnummer: 0,
          Währung: '',
          'Text Lohnart': columnText,
          Anzahl: columnAnzahlValue,
          'Mutationscode Anzahl': 1,
          Ansatz: columnAnsatzValue,
          'Mutationscode Ansatz': columnmutationsCodeAnsatzValue,
          ...sharedBetweenCodesData
        });
      }
    );
  });

  exportExcel({
    baseData: data,
    name: 'lohfile tempSTAFF',
    title: '',
    detailedTable: false,
    showTitle: false,
    autoFilter: false
  });
};

export const generateADPReportCSV = (props) => {
  if (props?.accountId === CHEFS_GARNED_CATERING_AND_EVENTS_ACCOUNT_ID) {
    const columns = Object.entries({
      'Co Code': 'Co Code',
      'Batch ID': 'Batch ID',
      employeeId: 'File #',
      name: 'Employee name',
      hourlyRate: 'Temp Rate',
      regular: 'Reg Hours',
      regEranings: 'Reg Earnings',
      overtime: 'O/T Hours',
      overtimeEranings: 'O/T Earnings',
      memoCode: 'Memo Code',
      memoAmount: 'Memo Amount',
      eventName: 'Temp Cost Number',
      rateCode: 'Rate Code',
      earnings3Code: 'Earnings 3 Code',
      earnings3Amount: 'Earnings 3 Amount',
      emptyCell: ''
    }).map(([column, label]) => ({ Header: label, dataKey: column }));

    return generateCSV({
      ...props,
      columns,
      summarized: false,
      rowTransformer: (row, extraRowsData) => {
        const gratuityFieldValue = extraRowsData?.payroll?.fields
          ? extraRowsData.payroll.fields?.find(({ id }) => id === 159)?.value
          : 0;

        const totalHours =
          Object.values(extraRowsData?.payroll?.aggregatedHours || {}).reduce((sum, value) => sum + value, 0) || 0;

        const data = {
          'Co Code': '0CT',
          'Batch ID': '',
          employeeId: (extraRowsData?.employee?.identification || '').toString(),
          name: [extraRowsData?.employee?.firstName || '', extraRowsData?.employee?.lastName || ''].join(' '),
          hourlyRate: row.hourlyRate,
          regular: row.regular,
          regEranings: '',
          overtime: row.overtime,
          overtimeEranings: '',
          memoCode: '',
          //total gratuityFieldValue
          memoAmount: gratuityFieldValue * totalHours,
          eventName: extraRowsData?.positionId
            ? (props?.mappedPositions?.[extraRowsData.positionId]?.positionGroups || [])
                .map(({ name }) => name)
                .join(', ') || ''
            : '',
          rateCode: '',
          earnings3Code: '',
          earnings3Amount: '',
          emptyCell: ''
        };

        return Object.fromEntries(columns.map(({ dataKey }) => [dataKey, data[dataKey] || '']));
      }
    });
  }

  if (props?.accountId === OBE_ACCOUNT_ID) {
    return generateOBEADPReportCSV(props);
  }
};

const roundHours = (value) => Number((value || 0).toFixed(2));

const generateOBEADPReportCSV = ({
  t,
  from,
  to,
  timesheetData,
  mapPayroll,
  clientMapPayroll,
  mapPayrollWithLabourCost,
  filterDataFunction,
  search,
  filters,
  accountName,
  handleError,
  addDummy = false
}) => {
  const columns = Object.entries({
    'Co Code': 'Co Code',
    'Batch ID': 'Batch ID',
    employeeId: 'File #',
    name: 'Employee name',
    eventName: 'Temp Cost Number',
    hourlyRate: 'Temp Rate',
    regular: 'Reg Hours',
    overtime: 'O/T Hours',
    overtime2: 'Hours 3 Code',
    hourst3Amount: 'Hours 3 Amount'
  }).map(([column, label]) => ({ Header: label, dataKey: column }));

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    pto: timesheetData?.pto,
    leaveRecords: timesheetData?.leave,
    employees: timesheetData?.employees,
    filterDataFunction,
    groupByKey: 'employee',
    mapPayroll,
    clientMapPayroll,
    mapPayrollWithLabourCost,
    search,
    filters,
    summarized: false,
    addDummy
  });

  if (groupedData?.length <= 0 && handleError) {
    handleError(t('noDataToExport'));
    return;
  }

  const dateSorter = SORT_BY_DATE('date');
  const rows = groupedData
    .flatMap(({ subRows }) => subRows.map((row) => ({ ...row, date: row.shiftDate || row.date })))
    .sort((a, b) => SORT_BY_NAME(a, b) || dateSorter(a, b));

  // ADP expects one line per worker per event, so multi-day events are aggregated across their shifts.
  // Split by rate so "Temp Rate" stays accurate; rows without an event are kept as-is.
  const aggregatedRows = new Map();

  rows.forEach((row, index) => {
    const hourlyRate = row.wage?.hourlyWage || 0;
    const key = row.projectId ? `${row.employee?.id}|${row.projectId}|${hourlyRate}` : `row-${index}`;
    const { regular = 0, overtime = 0, overtime2 = 0 } = row.payroll?.aggregatedHours || {};

    const current = aggregatedRows.get(key) || {
      'Co Code': 'J7D',
      'Batch ID': 'import',
      employeeId: (row.employee?.identification || '').toString(),
      name: [row.employee?.firstName || '', row.employee?.lastName || ''].join(' '),
      /* "Project Number" =  the first 10 characters from the event's name */
      eventName: row.name ? row.name.slice(0, 10) : 'ADDNAME',
      hourlyRate,
      regular: 0,
      overtime: 0,
      overtime2: 0,
      hourst3Amount: 0
    };

    aggregatedRows.set(key, {
      ...current,
      regular: current.regular + regular,
      overtime: current.overtime + overtime,
      overtime2: current.overtime2 + overtime2,
      /* "Hours 3 Amount"= (overtime2 X multiplier X TempRate) */
      hourst3Amount: current.hourst3Amount + overtime2 * hourlyRate * (row.multipliers?.overtime2 || 1)
    });
  });

  const exportedData = [...aggregatedRows.values()].map((row) => {
    const data = {
      ...row,
      regular: roundHours(row.regular),
      overtime: roundHours(row.overtime),
      overtime2: roundHours(row.overtime2),
      hourst3Amount: roundHours(row.hourst3Amount)
    };

    return conditionalColumns(
      Object.fromEntries(columns.map(({ dataKey }) => [dataKey, data[dataKey] || ''])),
      columns
    );
  });

  exportExcel({
    baseData: exportedData,
    name: [accountName, t('detailedPayrollReport'), from, to].join(' - '),
    showTitle: false
  });
};

// Only include these specific timesheet field IDs for Artisan export
// 59037 - Sales
// 59038 - Units Sold
// 59039 - Customers Served
// 59040 - Target
// 59041 - "Approvers Name"

const ARTISAN_TIMESHEET_FIELD_IDS = [59037, 59038, 59039, 59040, 59041];
const ARTISAN_SALES_TIMESHEET_FIELD_ID = 59037;
const ARTISAN_UNITS_SOLD_TIMESHEET_FIELD_ID = 59038;
const ARTISAN_CUSTOMERS_SERVED_TIMESHEET_FIELD_ID = 59039;
const ARTISAN_TARGET_TIMESHEET_FIELD_ID = 59040;

export const generateArtisanReportCSV = ({
  mappedLabels,
  mappedLocations,
  filters,
  timesheetData,
  mapPayroll,
  clientMapPayroll,
  search,
  mappedBranches,
  mappedAdmins,
  mappedPositions,
  formattingMap,
  account,
  timeFormat,
  mappedClientBranches,
  mappedClients,
  timesheetFields = [],
  projectFieldDefs = [],
  t
}) => {
  // Filter timesheet fields to only include specific IDs for Artisan
  const artisanTimesheetFields = (timesheetFields || []).filter(({ id }) => ARTISAN_TIMESHEET_FIELD_IDS.includes(id));

  // Pre-compute name→id and id→name maps for the 4 fields placed at fixed row positions
  const artisanPositionedFieldIds = new Set([
    ARTISAN_SALES_TIMESHEET_FIELD_ID,
    ARTISAN_UNITS_SOLD_TIMESHEET_FIELD_ID,
    ARTISAN_CUSTOMERS_SERVED_TIMESHEET_FIELD_ID,
    ARTISAN_TARGET_TIMESHEET_FIELD_ID
  ]);
  const artisanTimesheetFieldNameById = artisanTimesheetFields.reduce(
    (acc, { id, name }) => ({ ...acc, [id]: name }),
    {}
  );

  const groupedData = groupByData({
    jobs: [],
    slots: timesheetData?.slots || [],
    employees: timesheetData?.employees,
    groupByKey: 'employee',
    clientMapPayroll,
    summarized: false,
    search,
    filters,
    mapPayroll,
    mappedLocations,
    mappedLabels
  });

  const getFirstWeekDayOfDate = (value) => {
    const startOfShiftWeek = value.startOf('week');
    const { firstDayOfWeek, dateFormat = 'DD.MM.YYYY' } = account;
    switch (startOfShiftWeek.day()) {
      // Moment says that the first day of the week is Sunday (0)
      case 0:
        // But my first day of the week is different, for example Monday (2) or Friday (5)
        if (firstDayOfWeek !== 1) {
          // So I need to add the difference
          return startOfShiftWeek.add(firstDayOfWeek - 1, 'days').format(dateFormat);
        }
        break;
      // Moment says that the first day of the week is Monday (1)
      case 1:
        // But my first day of the week is different, for example Sunday (2) or Wednesday (4)
        if (firstDayOfWeek === 1) {
          // So I need to subtract the difference, 2 - 1 = 1, we subtract 1 day from Monday to get Sunday
          return startOfShiftWeek.subtract(1, 'days').format(dateFormat);
        } else if (firstDayOfWeek !== 2) {
          // So I need to add the difference, 4 - 2 = 2, we add 2 days to Monday to get Wednesday
          return startOfShiftWeek.add(firstDayOfWeek - 2, 'days').format(dateFormat);
        }
        break;
      default:
        break;
    }

    return startOfShiftWeek.format(dateFormat);
  };

  // Seed from system-defined fields (all branches) so columns appear even when no row has a value.
  // Then layer in data-derived fields to catch any that were deleted from the system but still have values.
  const allCustomFields = (groupedData || [])
    .flatMap((item) => item.subRows)
    .flatMap((subRow) => subRow.projectFields || [])
    .reduce(
      (acc, { fieldId, name }) => (acc.has(fieldId) ? acc : acc.set(fieldId, name)),
      new Map(projectFieldDefs.map(({ fieldId, name }) => [fieldId, name]))
    );

  const visibleData = (groupedData || []).reduce((output, item) => {
    item.subRows.forEach((subRow) => {
      const {
        bookerId,
        employee,
        shiftDate,
        positionId,
        actualIn,
        actualOut,
        adminApproved,
        payroll,
        name: projectName,
        invoiceNumber,
        branchId,
        locationId,
        tags,
        comments,
        clientBranchId,
        clientId,
        projectComments,
        projectPublicComments,
        clientPayroll,
        payrollProcess = {},
        projectFields = []
      } = subRow;

      const { identification = '', firstName = '', lastName = '' } = employee || {};
      const { wage = {}, aggregatedHours = {}, value: totalPayroll = 0 } = payroll || {};
      const { wage: clientWage = {}, value: totalBillPay = 0 } = clientPayroll || {};

      const timesheetApproverName = adminApproved
        ? `${mappedAdmins?.[adminApproved.adminId]?.firstName} ${mappedAdmins?.[adminApproved.adminId]?.lastName}`
        : '';

      const payrollProcessByName = payrollProcess?.createdBy
        ? `${mappedAdmins?.[payrollProcess.createdBy]?.firstName} ${mappedAdmins?.[payrollProcess.createdBy]?.lastName}`
        : '';

      const totalHours = Object.values(aggregatedHours).reduce((sum, curr) => sum + (curr || 0), 0);

      const shiftMoment = moment(shiftDate, API_DATE_FORMAT);
      const eventBookerName = [mappedAdmins?.[bookerId]?.firstName, mappedAdmins?.[bookerId]?.lastName]
        .filter((curr) => !!curr)
        .join(' ');

      const customFieldValues = [...allCustomFields.entries()].reduce((acc, [fieldId, name]) => {
        const field = projectFields.find((f) => f.fieldId === fieldId);

        //not overwrite with empty if a value was already set:
        acc[name] = field?.formattedValue || acc[name] || '';
        return acc;
      }, {});

      const timesheetExportFields = Object.fromEntries(
        Object.entries(
          getTimesheetFieldsForExport({
            row: subRow,
            timesheetFields: artisanTimesheetFields,
            t,
            dateFormat: account?.dateFormat
          })
        ).filter(([key]) => key !== 'sequencesResultsCompletedFieldsStatus')
      );

      const salesColName = artisanTimesheetFieldNameById[ARTISAN_SALES_TIMESHEET_FIELD_ID];
      const unitsSoldColName = artisanTimesheetFieldNameById[ARTISAN_UNITS_SOLD_TIMESHEET_FIELD_ID];
      const customersServedColName = artisanTimesheetFieldNameById[ARTISAN_CUSTOMERS_SERVED_TIMESHEET_FIELD_ID];
      const targetColName = artisanTimesheetFieldNameById[ARTISAN_TARGET_TIMESHEET_FIELD_ID];

      const row = {
        Day: shiftMoment.format('dddd'),
        'W/C Date': getFirstWeekDayOfDate(shiftMoment.clone()),
        Date: formattingMap.date(shiftMoment),
        Client: mappedClientBranches?.[clientBranchId]?.name || '',
        'Client Ref': mappedClients?.[clientId]?.supplierNotes || '',
        Position: positionId ? mappedPositions[positionId].slug : '',
        'Event Tags': (tags || []).map(({ title }) => title).join(', '),
        'Job Code': 'BRET',
        'Employee Name': `${firstName} ${lastName}`,
        'Employee Code': identification,
        Hours: totalHours, // (breaks deducted)
        Start: actualIn ? moment(actualIn).format(timeFormat) : '',
        Finish: actualOut ? moment(actualOut).format(timeFormat) : '',
        'Invoice ID': invoiceNumber,
        Location: mappedLocations[locationId]?.name,
        Store: mappedLocations[locationId]?.name,
        Pay: formattingMap.currency(wage.hourlyWage || 0),
        Charge: formattingMap.currency(clientWage.hourlyWage || 0),
        'Total Charge': formattingMap.currency(totalBillPay || 0),
        'Comments For Admin': projectComments,
        'Event Name': projectName,
        'Event Booker': eventBookerName,
        Rate: totalHours === (aggregatedHours.regular || 0) ? 1 : 2,
        ...(salesColName && { [salesColName]: timesheetExportFields[salesColName] ?? '' }),
        Branch: mappedBranches[branchId]?.name,
        ...(unitsSoldColName && { [unitsSoldColName]: timesheetExportFields[unitsSoldColName] ?? '' }),
        'General Comments (shown to employees)': projectPublicComments || '',
        Notes: comments,
        'Approved by': timesheetApproverName,
        'Processed by': payrollProcessByName,
        'Processed at': payrollProcess?.createdAt ? formattingMap.dateAndTime(payrollProcess.createdAt) : '',
        ...(customersServedColName && {
          [customersServedColName]: timesheetExportFields[customersServedColName] ?? ''
        }),
        ...(targetColName && { [targetColName]: timesheetExportFields[targetColName] ?? '' }),
        'Total Payroll': formattingMap.currency(totalPayroll),
        ...Object.fromEntries(
          Object.entries(timesheetExportFields).filter(
            ([key]) => !artisanPositionedFieldIds.has(artisanTimesheetFields.find((f) => f.name === key)?.id)
          )
        ),
        ...customFieldValues
      };

      output.push(row);
    });

    return output;
  }, []);

  exportExcel({
    baseData: visibleData || [],
    name: 'Artisan payroll export'
  });
};

export const generalPayrollCintraReportCSV = ({
  t,
  accountId,
  dateFormat,
  timesheetData,
  mapPayroll,
  clientMapPayroll,
  filterDataFunction,
  search,
  filters,
  from,
  to
}) => {
  if (accountId !== SURREY_COUNTY_CRICKET_CLUB) {
    return;
  }

  const groupedData = groupByData({
    jobs: timesheetData?.jobs,
    slots: timesheetData?.slots,
    employees: timesheetData?.employees,
    filterDataFunction,
    groupByKey: 'employee',
    mapPayroll,
    clientMapPayroll,
    search,
    filters,
    summarized: false
  });

  const columns = Object.entries({
    identification: 'Employment ID',
    firstName: 'First Name',
    lastName: 'Surname',
    header: 'Header',
    totalHours: 'Units',
    totalPayroll: 'Rate / Amount',
    fromDate: 'From Date',
    toDate: 'To Date',
    post: 'Post',
    notes: 'Notes',
    costCenter: 'Cost Code 1',
    costCode2: 'Cost Code 2'
  }).map(([dataKey, Header]) => ({ dataKey, Header }));

  const dateSorter = SORT_BY_DATE('date');
  const sorter = (a, b) => SORT_BY_NAME(a, b) || dateSorter(a, b);

  const visibleData = groupedData
    .reduce((prev, { subRows, groupValue }) => [...prev, ...subRows.map((row) => ({ ...row, groupValue }))], [])
    .filter((row) => row.actualOut && row.actualIn)
    .sort(sorter)
    .map((row) => {
      const { employee, shiftDate, date, actualTotal, payroll, costCenter } = row || {};
      const { firstName = '', lastName = '', identification = '' } = employee || {};

      const totalHours = actualTotal ? Number(moment.duration(actualTotal).asHours().toFixed(2)) : '';

      return conditionalColumns(
        {
          identification,
          firstName,
          lastName,
          fromDate: moment(shiftDate || date, API_DATE_TIME_FORMAT)
            .startOf('week')
            .format(dateFormat),
          toDate: moment(shiftDate || date, API_DATE_TIME_FORMAT)
            .endOf('week')
            .format(dateFormat),
          totalHours,
          totalPayroll: '',
          costCenter: costCenter?.identifier ?? '',
          header: payroll?.wage?.hourlyWage || '',
          post: '',
          notes: '',
          costCode2: ''
        },
        columns
      );
    });

  exportExcel({
    baseData: visibleData || [],
    name: `${t('cintraPayrollReport')} ${from} - ${to}`,
    showTitle: false
  });
};

export const isInvoicesAllClientsSelected = (clientId) => clientId === -1;
